
import { defineComponent } from "vue";
import * as fb from "../../firebase";

import DraftPreviewTable from "./migration/DraftPreviewTable.vue";
import MmiDraftPreview from "./migration/MmiDraftPreview.vue";
import InvariantGate from "./migration/InvariantGate.vue";
import StepAuto from "./migration/StepAuto.vue";
import CatalogCheck from "./migration/CatalogCheck.vue";
import type { CatalogReport } from "./migration/catalogTypes";
import CofounderRanks from "./migration/CofounderRanks.vue";
import type {
  CofounderReport,
  CofounderResult,
  CofounderWrite,
} from "./migration/cofounderTypes";
import {
  GateReport,
  GetRunPayload,
  ListRunsPayload,
  MigrationCloudTool,
  MigrationDraft,
  MigrationFreezeState,
  MigrationRun,
  MigrationStepDef,
  MigrationStepState,
  MigrationStepStatus,
  ImportRowState,
  ImportRowSummary,
  CuadreLeg,
  MigrationStepInstance,
  PointsReport,
  PointsReportPayload,
  PointsReportRow,
  ReportConfirmation,
  stepIsUnlocked,
} from "./migration/types";
// EL VOCABULARIO DE NIVEL, DE SU ÚNICA FUENTE. `packageIds.ts` no importa nada
// (ni firebase-admin ni nada del runtime de functions): son constantes y cuatro
// funciones puras, así que entra al bundle del navegador sin arrastrar cola. Es
// el mismo módulo del que `corte.ts` saca el `ANCHOR_PACKAGES` y el
// `isAnchorPackage` que valida el backend — por eso esto ya no puede derivar.
import {
  PACKAGE_LABELS,
  isPackageLabel,
} from "@functions/Subscriptions/packageIds";
// EL ÍNDICE DE INVARIANTES, del mismo sitio y por lo mismo: es un módulo sin
// una sola importación, y `tests/network-reset/invariantCatalog.spec.ts` lo
// compara contra los dos motores fila a fila, así que no puede derivar de ellos.
import {
  GATE_INVARIANT_CATALOG,
  UNFREEZE_INVARIANT_CATALOG,
} from "@functions/Subscriptions/Activations/migration/invariants/catalog";
import type { InvariantCatalogEntry } from "@functions/Subscriptions/Activations/migration/invariants/catalog";
import { renderStepProse } from "./migration/stepProse";

/**
 * The migration console — the §11 runbook, executable.
 *
 * It earns its place over a markdown checklist by doing three things a document
 * cannot: it FORCES the order, it gives the draft a review cycle (calcular →
 * revisar → aprobar → aplicar), and it turns the paso-16 gate into a semáforo
 * that will not let you unfreeze on red.
 *
 * It is a CLIENT, though. Every button here is a thin wrapper over a callable
 * that is invocable from a shell without it — if this page breaks on go-live day,
 * the migration carries on.
 */
/**
 * The shape resolveDraft expects (SeedAnchor). Tier is derived from `package`.
 *
 * ⚠️ `package` LLEVA UN PAQUETE, NO UNA SUSCRIPCIÓN. Son dos vocabularios que se
 * parecen lo justo para confundirlos: el paquete es `gtr-fly`, y el documento de
 * suscripción que le corresponde es `trading-academy` (`placementSubscriptionOf`)
 * — y ojo, `gtr-fly` TAMBIÉN es un doc-id de esa misma familia, así que se parecen
 * todavía más que antes. Esta plantilla decía `"trading-academy"` en sus DOS
 * ejemplos — o sea, la consola le enseñaba al operador el valor equivocado, y
 * "funcionaba" sólo porque `tierOfPackage` lo dejaba caer por su `default` y lo
 * sembraba como `full`. Desde el 2026-07-22 el backend lo RECHAZA, y hace bien: el
 * paquete decide el tier, y el tier decide si el ancla entra al binario.
 *
 * 🆕 VOCABULARIO NUEVO (2026-07-25). Los ejemplos van con las etiquetas VIVAS
 * (`gtr-fly`, antes `gtr-basic`). Un payload de anclas ya escrito con las
 * retiradas SIGUE VALIENDO —`isAnchorPackage` acepta las dos generaciones a
 * propósito, porque esto se teclea a las 3am— pero lo que se ENSEÑA son las vivas.
 */
const ANCHORS_PLACEHOLDER = `[
  {
    "username": "gtr.master",
    "package": "gtr-fly",
    "enroller": "rootuser",
    "binaryParentPK": "rootuser",
    "binarySide": "left"
  },
  {
    "username": "pitgtr",
    "package": "gtr-fly",
    "enroller": "gtr.master",
    "binaryParentPK": "gtr.master",
    "binarySide": "left"
  }
]`;

/**
 * Los `package` que se ENSEÑAN junto al textarea de anclas: el vocabulario no se
 * adivina a las 3am.
 *
 * 🔴 NO SE ESCRIBEN AQUÍ. Salen de `packageIds.ts`, que es el ÚNICO sitio del
 * sistema donde vive el vocabulario de nivel — el mismo del que salen el
 * `ANCHOR_PACKAGES` y el `isAnchorPackage` del backend (`corte.ts` los declara
 * como alias de estos dos). Hasta el 2026-07-25 esta línea era una COPIA con las
 * etiquetas retiradas (`gtr-basic`/`gtr-lite`/`gtr-vip`): el backend ya enseñaba
 * las nuevas y la consola seguía ofreciendo las viejas, así que quien mirase la
 * pantalla se llevaba el nombre equivocado.
 *
 * ⚠️ ENSEÑAR Y ACEPTAR NO SON LO MISMO, y ésta es la mitad de ENSEÑAR. La
 * validación de abajo usa `isPackageLabel`, que acepta TAMBIÉN las retiradas —
 * hay payloads de anclas ya escritos, y que se acepten no cuesta nada mientras
 * que plantarse costaría un paso del runbook.
 */
const ANCHOR_PACKAGES: readonly string[] = PACKAGE_LABELS;

/**
 * SOLO UN EJEMPLO EN GRIS. El textarea arranca VACÍO — ver el porqué abajo.
 *
 * ESTE JSON ES LA ÚNICA FUENTE DE LOS ALIAS (Hector, 2026-07-26). `resolveDraft`
 * comparaba lo que se tecleara contra `ACCOUNT_ALIASES` (EnrollerChain.ts) y se
 * negaba si no coincidían en los dos sentidos; esa constante se retiró para que
 * añadir un par no exija un despliegue, y con ella se fue el rechazo.
 *
 * Lo que sigue en pie —y ahora sin red— es la consecuencia que aquel rechazo
 * evitaba: un par que falte aquí le da a esa persona DOS posiciones en el árbol,
 * en silencio y para siempre. Antes se plantaba; hoy se aplica.
 *
 * ⚠️ POR QUÉ ESTO YA NO PRE-RELLENA EL TEXTAREA (2026-07-26)
 * ---------------------------------------------------------------------------
 * Porque LOS ALIAS DEPENDEN DEL ENTORNO. En el emulador y en sandbox son dos;
 * en producción son TRES, porque `laurapelayo` → `patybrambila` es un renombre
 * que entra dentro de la ventana y que en el emulador no existe.
 *
 * Un valor por defecto correcto en un entorno y equivocado en otro es PEOR que
 * no tener ninguno, porque falla en silencio: quien corriera producción vería
 * el textarea ya lleno con los dos pares del emulador, lo daría por revisado, y
 * `laurapelayo` se quedaría SIN NODO. Eso no truena, no lo caza ningún
 * invariante, y sólo se ve mirando el árbol ya construido.
 *
 * Que el operador tenga que ir a buscar el fichero de SU entorno
 * (`documentation/implementations/2026-07-26-payloads-paso15/`) no es la
 * molestia: es la garantía.
 */
const ALIASES_PLACEHOLDER = `{
  "pflores": "pitgtr",
  "gtr.academy": "gtr.master"
}`;

/**
 * Los pasos que piden ANCLAS + ALIAS. Hoy es UNO: el que siembra el árbol.
 *
 * Estuvo también el reporte de corte, y ésa era la señal de que el reporte
 * hacía el trabajo de la siembra: pedía las anclas porque RESOLVÍA el árbol en
 * memoria para poder decir qué nodos invertían. Desde el 2026-07-22 sólo LEE el
 * árbol ya construido, así que no tiene nada que teclear.
 */
const STEPS_WITH_ANCHORS = ["build-trees--c"];

/**
 * Cuánto espera el CLIENTE antes de rendirse. Va atado a `timeoutSeconds` de las
 * callables de migración (540 s, `functions/src/System/Migration/MigrationFuncs.ts`).
 *
 * Si allí sube, aquí sube: el reloj del cliente por debajo del del servidor es lo
 * que produce un `deadline-exceeded` con la operación todavía viva por detrás.
 * El porqué largo está en `call()`.
 */
const CALLABLE_TIMEOUT_MS = 540_000;

/**
 * El paso del reporte de corte, y el que lo CONSUME (la captura a mano). Los dos
 * van ANTES de descongelar: la red debe abrir en su estado final.
 *
 * ⚠️ SON IDS, NO NÚMEROS (2026-07-24). Antes aquí había un `"23"` y un `"24"`, y
 * cada renumeración los dejaba apuntando a otro paso — el botón acababa llamando
 * a `undefined`, o pidiéndole permiso al paso equivocado. Estas claves no se
 * mueven: el número que se pinta sale de `labelOf()`.
 */
/**
 * El paso «Validar el catálogo nuevo», que cuelga un panel de LECTURA.
 *
 * ID, no número, por lo mismo que los de abajo: cada renumeración dejaba a un
 * número apuntando a otro paso.
 *
 * ⚠️ Y aquí el nombre de la callable va en el FRONT, no en `step.callable` como
 * en el reporte de puntos. No es un atajo: este paso es MANUAL —su trabajo real
 * son dos booleanos que se tocan a mano— y no debe declarar `callable`, porque
 * entonces la consola le pintaría un botón «Ejecutar» a un paso que no ejecuta
 * nada. Lo que cuelga de la tarjeta es un lector, no la acción del paso.
 */
const CATALOG_STEP_ID = "exclude-lite-binary--c";
const CATALOG_CALLABLE = "VerifyCatalog";

/**
 * LOS DOS PASOS QUE EMITEN UN VEREDICTO, y el listado de invariantes de cada uno.
 *
 * El del 18 es el estructural («descongelar o rollback»); el otro es el que se
 * pasa justo antes de abrir la red, diez pasos después. Los dos devuelven el
 * mismo informe y desde el 2026-07-26 los dos se pintan con el mismo componente,
 * con SU listado — que es lo que permite enseñar las 33 (o las 4) en gris desde
 * el instante en que se pulsa, sin esperar al informe.
 *
 * Los catálogos vienen del backend (`@functions/...invariants/catalog`), que es
 * de donde salen las invariantes de verdad, y una spec de la suite comprueba que
 * digan lo mismo que los motores. Una lista de ids escrita aquí a mano habría
 * sido justo la deriva que esta consola existe para evitar.
 */
const GATE_STEPS = ["gate--c", "gate-unfreeze--c"];
const GATE_CATALOGS: Record<string, InvariantCatalogEntry[]> = {
  "gate--c": GATE_INVARIANT_CATALOG,
  "gate-unfreeze--c": UNFREEZE_INVARIANT_CATALOG,
};

/**
 * El mismo diccionario SIN una clave — una copia nueva, que es lo que Vue
 * necesita para repintar. Se usa para OLVIDAR lo de la corrida anterior en
 * cuanto empieza la siguiente (ver `guardedStep`).
 */
function sinLaClave<T>(
  mapa: Record<string, T>,
  clave: string
): Record<string, T> {
  const copia = { ...mapa };
  delete copia[clave];
  return copia;
}

/**
 * El paso del rango COFUNDADOR y su porcentaje de binario.
 *
 * ⚠️ EL NOMBRE DE LA CALLABLE VA AQUÍ, no en `step.callable`, y por el mismo
 * motivo que el del catálogo: el paso es MANUAL —lo cierra la persona, con su
 * tiempo, cuando ya miró la tabla y confirmó la lista con Hector— así que no
 * debe declarar `callable`. Si lo hiciera, la consola le pintaría un botón
 * «Ejecutar» genérico donde tiene que haber una tabla con su antes y su después.
 *
 * El botón sí ESCRIBE (a diferencia del lector del catálogo), pero escribir no
 * es completar el paso: son las dos escrituras que antes se tecleaban a mano en
 * la consola de Firestore, ni una más.
 */
const COFOUNDER_STEP_ID = "cofounder-rank--c";
const COFOUNDER_CALLABLE = "CofounderRank";

/**
 * El paso del reporte de corte — y, desde el 2026-07-26, también el de la
 * RESTITUCIÓN de los saldos.
 *
 * Aquí había una segunda constante, `REPORT_CONSUMER_STEP_ID`, apuntando a
 * el id restore-binary-points--c: el paso que CONSUMÍA el reporte tecleando los
 * saldos a mano, y en cuya tarjeta se repintaba esta misma tabla. Ese paso se
 * retiró porque su trabajo se hace ya desde aquí, renglón a renglón, así que no
 * queda un segundo sitio donde montar la tabla. Su id es una LÁPIDA y no se
 * recicla.
 */
const REPORT_STEP_ID = "points-report--c";

/**
 * EL PASO QUE PRODUCE EL SELLO DEL EXPORT. Su comando devuelve la ruta del
 * backup, así que es el único que pinta el campo en su pie — en los demás sería
 * ruido, porque ahí el dato no nace.
 */
const BACKUP_STEP_ID = "create-backup--c";

/**
 * LAS DOS CUENTAS DE PIERNA INFINITA que reciben la concesión (Hector,
 * 2026-07-26). Son las fundadoras, y su saldo de binario NO se ganó: se OTORGA.
 * Es el mecanismo que hace que su binario siempre tenga con qué aparear, y por
 * eso nunca tuvieron registros de detrás — no hay nada que copiar, sólo que
 * crear.
 *
 * ⚠️ SON DOS, NO LAS SEIS de `BINARY_EXPIRATION_EXEMPT`. Aquella lista dice
 * «a éstas no les caducan los puntos», que es otra cosa: `monicamenmal` y
 * `maph01` no vencen y AUN ASÍ no reciben esta concesión. Confundirlas sería
 * regalar novecientos millones a dos cuentas de uso diario.
 */
const PIERNAS_INFINITAS = ["gtr.master", "pitgtr"];
/** Lo que se PROPONE crear en su derrame. Editable antes de confirmar. */
const PUNTOS_INFINITOS = 900_000_000;

/**
 * EL ICONO DE CADA HERRAMIENTA (Hector, 2026-07-25): «firebase puede usar fire,
 * bq algo de bd, gcloud una nube, y así». Los cinco son classic solid de
 * FontAwesome Pro 7, que es el estilo que carga `main.ts`.
 *
 * `Record` completo, sin `?`: si mañana entra una herramienta nueva en
 * `MigrationCloudTool`, TypeScript pide su icono aquí en vez de dejar el hueco.
 */
const TOOL_ICONS: Record<MigrationCloudTool, string> = {
  gcloud: "fa-cloud",
  bq: "fa-database",
  gsutil: "fa-bucket",
  "firebase-deploy": "fa-fire",
  extensions: "fa-puzzle-piece",
};

/**
 * Los proyectos GCP a los que puede apuntar un run. `<PROJECT>` es EL
 * placeholder de los comandos manuales: sale 30 veces en `steps.ts` y hasta
 * ahora se tecleaba a mano, comando por comando, a las 3am y con el reloj
 * congelado. Es un dato del RUN ENTERO, no de un paso: por eso el selector vive
 * en la cabecera y no dentro de cada tarjeta.
 *
 * `prod: true` NO es decorativo — es lo que enciende el rojo en toda la página.
 * `gtr-sandbox` va primero y es el DEFAULT a propósito: el fallo que hay que
 * hacer imposible es copiar un comando de producción creyendo que va al ensayo,
 * así que el estado en el que arranca la consola es el inofensivo.
 */
const MIGRATION_PROJECTS: {
  id: string;
  label: string;
  prod: boolean;
  /**
   * EL BUCKET DE BACKUPS DE ESTE PROYECTO — el `<BUCKET>` de los comandos, que
   * sale tres veces: una en el export del backup y dos en el doble import.
   *
   * Que esté aquí NO es comodidad. En producción existe OTRO bucket con nombre
   * parecido, `gtr-academy-database-backup`, y lo más reciente que guarda es de
   * 2022. Con los dos nombres a la vista, de madrugada y con el sistema
   * congelado, apuntar el import al viejo es un error perfectamente plausible —
   * y lo peor es que no falla: el `gcloud firestore import` corre igual, el LRO
   * termina bien, y la red nueva se construye sobre datos de hace cuatro años.
   * Nada de lo que viene después lo detecta. Por eso el nombre lo pone esta
   * tabla y no la memoria de nadie a las 3am.
   */
  bucket: string;
}[] = [
  {
    id: "gtr-sandbox",
    label: "gtr-sandbox — ensayo",
    prod: false,
    bucket: "gtr_academy-sandbox_db_backup",
  },
  {
    id: "gtr-academy",
    label: "gtr-academy — ⚠ PRODUCCIÓN",
    prod: true,
    bucket: "gtr_academy-production_db_backup",
  },
];

/**
 * El bucket viejo de prod. NO se usa para nada: está aquí para poder NOMBRARLO
 * en el aviso, porque un aviso que dice «cuidado, hay otro parecido» sin decir
 * cuál obliga al operador a buscarlo, y buscarlo es abrir la consola de GCS con
 * los dos delante — exactamente la situación que queremos evitar.
 */
const LOOKALIKE_PROD_BUCKET = "gtr-academy-database-backup";

/** Lo que el selector sustituye. Sólo esto: el resto de placeholders se queda a la vista. */
const PROJECT_PLACEHOLDER = "<PROJECT>";
/** El bucket de backups, que sale del MISMO selector: es un dato del proyecto. */
const BUCKET_PLACEHOLDER = "<BUCKET>";
/**
 * La fecha del run, que TAMBIÉN es un valor conocido (la consola ya la pinta
 * arriba, y el gate del paso 18 la compara al milisegundo). No se inventa nada:
 * si el run no la trae, el placeholder se queda como estaba.
 */
const DATE_PLACEHOLDER = "<FECHA-DE-MIGRACION-DEL-RUN>";

/**
 * ==================== EL SELLO DE FECHA DEL EXPORT ==========================
 *
 * El paso del backup lanza `gcloud firestore export
 * gs://<BUCKET>/pre-reset-$(date +%Y%m%d-%H%M)`. Ese `$(date …)` NO lo resuelve
 * esta consola ni el runbook: lo resuelve LA TERMINAL, en el instante en que el
 * operador pulsa enter. O sea que el nombre real de la carpeta del backup nace
 * fuera de aquí y, hasta ahora, sólo existía en un sitio: la pantalla del
 * operador.
 *
 * Después, el paso del doble import pide esa MISMA ruta dos veces. Y ahí está
 * el problema: si el sello se teclea mal, el import no protesta. Apunta a otro
 * export —o a una carpeta que no existe— y el comando sigue siendo válido. Es
 * el fallo más caro de la ventana, porque todo lo que viene detrás (el barrido,
 * el prune, los árboles, el gate) se construye encima de esos datos, y para
 * cuando algo huela raro ya no hay ventana.
 *
 * La solución es la mínima posible: un campo donde PEGAR lo que devolvió la
 * terminal, y que el import lo interpole solo. La consola no lo compone ni lo
 * adivina — ver `normalizeExportStamp`.
 */
const EXPORT_STAMP_PLACEHOLDER = "<AAAAMMDD-HHMM>";

/**
 * El sello dentro de lo que sea que se haya pegado.
 *
 * Se acepta tanto el sello a secas (`20260725-0312`) como la ruta entera que
 * escupe la terminal (`gs://…/pre-reset-20260725-0312`), porque lo que el
 * operador tiene delante para copiar de un tirón es la ruta, no el trozo.
 *
 * Los bordes (`(?:^|[^0-9])` … `(?![0-9])`) no son adorno: sin ellos una cadena
 * con un dígito de más —`202607250-0312`, un dedazo perfectamente normal— sí
 * casaría, recortando por dentro y devolviendo un sello DISTINTO del que se
 * escribió. Un dato inventado que parece bueno es peor que no tener dato.
 */
const EXPORT_STAMP_RE = /(?:^|[^0-9])(\d{8}-\d{4})(?![0-9])/;

/**
 * Saca el sello de lo pegado, o "" si ahí dentro no hay ninguno.
 *
 * Devolver "" es una respuesta legítima y frecuente (el campo empieza vacío):
 * significa «todavía no lo sé», y la consola se comporta en consecuencia —
 * placeholder a la vista y copiar desactivado. Lo que NO se hace nunca es
 * componerlo con la hora de ahora: el export pudo terminar veinte minutos
 * antes, y un sello plausible pero falso apunta a una carpeta que no existe.
 */
function normalizeExportStamp(raw: string): string {
  return raw.trim().match(EXPORT_STAMP_RE)?.[1] ?? "";
}

/**
 * DÓNDE SE GUARDA EL SELLO, Y POR QUÉ NO EN EL RUN.
 *
 * El estado de la migración vive en Firestore (`migrationRuns/{runId}`) y sería
 * el sitio natural… salvo que esta página no escribe ahí. El índice de runs
 * vive en la base `migration-control`, a la que el front no tiene handle —
 * `firebase.ts` expone UNA instancia de Firestore, la de datos—, así que todo
 * lo que se persiste pasa por una callable. Meter el sello en el run sería
 * campo nuevo en el run + callable nueva + desplegar functions, y eso hay que
 * hacerlo dentro de la ventana o en su víspera.
 *
 * No compensa, porque el sello no es un dato de la migración: el backend no lo
 * mira nunca. Es la misma clase de cosa que el proyecto de los comandos — cómo
 * se PINTAN los comandos para copiarlos. Por eso se guarda en el navegador del
 * operador, que es donde vive el resto de esa clase de estado.
 *
 * El runId va EN LA CLAVE a propósito: sin él, el sello del ensayo aparecería
 * pre-rellenado en el run del go-live, que es la misma trampa que veníamos a
 * cerrar pero con la consola dándole la razón.
 *
 * Lo que esto aguanta: recargar la página, cerrar la pestaña, reabrir el
 * navegador. Lo que NO: cerrar sesión (el logout hace `localStorage.clear()`) y
 * cambiar de ordenador. Si conduce otra persona, el sello se vuelve a pegar —
 * por eso el paso del backup sigue diciendo «GUARDA LA RUTA».
 */
const EXPORT_STAMP_KEY = "gtr-migration-export-stamp";

function exportStampKeyFor(runId: string): string {
  return `${EXPORT_STAMP_KEY}:${runId}`;
}

function readExportStamp(runId: string): string {
  if (!runId) return "";
  try {
    return localStorage.getItem(exportStampKeyFor(runId)) ?? "";
  } catch {
    // Sin localStorage (modo privado, cuota llena) el campo sigue funcionando y
    // la sustitución también: lo único que se pierde es que aguante un refresco.
    return "";
  }
}

function writeExportStamp(runId: string, raw: string): void {
  if (!runId) return;
  try {
    if (raw) localStorage.setItem(exportStampKeyFor(runId), raw);
    else localStorage.removeItem(exportStampKeyFor(runId));
  } catch {
    // Ver `readExportStamp`: no persistir no es motivo para romper la página.
  }
}

/**
 * ========== QUÉ INSTANCIAS QUEDAN YA CONFIGURADAS ==========================
 *
 * Vive en el NAVEGADOR, por runId y por paso, exactamente como el sello del
 * export y por la misma razón: es estado de QUIEN ESTÁ RELLENANDO formularios en
 * otra consola, no un dato de la migración. El backend no lo necesita para nada
 * —la extensión no se instala desde aquí— y meterlo en el run costaría una
 * callable nueva para guardar un tic.
 *
 * LO QUE SÍ TENÍA QUE AGUANTAR es un refresco, y lo aguanta: son diez
 * formularios de 25 campos y la página se recarga sola más de una vez por
 * sesión. Aguanta recargar, cerrar la pestaña y reabrir el navegador. NO aguanta
 * cerrar sesión (el logout hace `localStorage.clear()`) ni cambiar de ordenador
 * — si conduce otra persona, las marcas se vuelven a poner.
 *
 * El paso va EN LA CLAVE además del runId: dos pasos con formularios no pueden
 * compartir marcas, y hoy hay uno pero el mecanismo no lo sabe.
 */
const INSTANCES_DONE_KEY = "gtr-migration-instances-done";

function instancesDoneKeyFor(runId: string, stepId: string): string {
  return `${INSTANCES_DONE_KEY}:${runId}:${stepId}`;
}

function readInstancesDone(runId: string, stepId: string): string[] {
  if (!runId) return [];
  try {
    const raw = localStorage.getItem(instancesDoneKeyFor(runId, stepId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // SE VALIDA LO LEÍDO. Es texto de fuera del programa: una clave pisada a
    // mano, o de una versión anterior, no puede tumbar la tarjeta del paso.
    return Array.isArray(parsed)
      ? parsed.filter((x): x is string => typeof x === "string")
      : [];
  } catch {
    return [];
  }
}

function writeInstancesDone(
  runId: string,
  stepId: string,
  ids: string[]
): void {
  if (!runId) return;
  try {
    if (ids.length) {
      localStorage.setItem(
        instancesDoneKeyFor(runId, stepId),
        JSON.stringify(ids)
      );
    } else {
      localStorage.removeItem(instancesDoneKeyFor(runId, stepId));
    }
  } catch {
    // Ver `readExportStamp`: no poder persistir no es motivo para romper nada.
  }
}

/**
 * LOS DOS CAMPOS QUE NO SE PUEDEN CAMBIAR DESPUÉS DE INSTALAR.
 *
 * Es la única advertencia de este bloque que NO se deduce mirando el formulario:
 * Firebase los pinta igual que a los otros veintitrés. Si uno sale mal, la salida
 * no es editar la instancia — es DESINSTALARLA y volver a crearla.
 *
 * Las etiquetas se comparan literales y en inglés porque así vienen del paso,
 * que a su vez las copia de la pantalla de Firebase.
 */
const IRREVERSIBLE_PARAMS = new Set([
  "Cloud Functions location",
  "BigQuery Dataset location",
]);

/**
 * ====== LA PROSA DE LAS FICHAS NO SE PINTA EN LA CONSOLA ====================
 *
 * EL PASO DE LAS EXTENSIONES TRAE LOS 250 CAMPOS DOS VECES: una en `instances`
 * —dato estructurado, que es lo que pintan las pestañas— y otra en el `detail`,
 * como diez fichas de texto. Las dos versiones tienen que existir: la de texto
 * es la que se lee en la hoja del operador y en el §11 del runbook, que no
 * tienen pestañas.
 *
 * EN LA CONSOLA SOBRA, y no es una cuestión de gusto: son diez fichas de
 * veinticinco líneas ANTES de las pestañas, así que la pantalla se convierte en
 * un muro y las pestañas quedan tan abajo que Hector no llegó a verlas —
 * literalmente, «no había llegado a las pestañas».
 *
 * SE CORTA AQUÍ Y NO EN `steps.ts` a propósito: allí el texto tiene lectores que
 * no tienen pestañas. Quitarlo de la fuente dejaría la hoja del operador sin los
 * campos.
 *
 * QUÉ SE CORTA, exactamente: la cabecera de cada ficha —la línea de rayas con el
 * id y la acción— y las líneas de campo que le siguen. NADA MÁS. Todo lo demás
 * del `detail` se queda tal cual: el aviso de `ext:export` antes del rename, el
 * momento seguro para borrar y recrear, los campos irreversibles, lo que no se
 * toca. Eso no está duplicado en ninguna pestaña.
 */
// LA CABECERA DE UNA FICHA: rayas, un espacio, y algo que no es otra raya.
//
// ⚠️ NO se exige que TERMINE en rayas, y no es descuido — se probó contra la
// prosa de verdad y una de las diez no las lleva: el nombre largo de
// `subscriptions` («RENOMBRAR desde firestore-bigquery-export-subscriptions») se
// comió el cierre. Con el cierre obligatorio, esa ficha sobrevivía al corte y se
// quedaba sola en mitad de la prosa, que es peor que no cortar nada.
//
// Y el espacio después de las rayas es lo que distingue una cabecera de un
// SEPARADOR a secas (`─────────…`), del que hay uno más abajo en este mismo
// paso y que no se toca: ahí, tras las dos primeras rayas, viene otra raya.
const FICHA_HEADER_RE = /^\s*──\s+[^─\s]/;

export function stripInstanceFiches(detail: string): string {
  const lineas = detail.split("\n");
  const salida: string[] = [];
  let avisoPuesto = false;
  for (let i = 0; i < lineas.length; i += 1) {
    if (!FICHA_HEADER_RE.test(lineas[i])) {
      salida.push(lineas[i]);
      continue;
    }
    // Una ficha: su cabecera y las líneas de campo que la siguen (sangradas con
    // seis espacios) más las líneas en blanco de entre medias. Se para en la
    // primera línea que no es ninguna de las dos cosas, que es donde vuelve a
    // empezar la prosa de verdad.
    i += 1;
    while (
      i < lineas.length &&
      (lineas[i].trim() === "" || /^ {6}\S/.test(lineas[i]))
    ) {
      i += 1;
    }
    i -= 1;
    if (!avisoPuesto) {
      avisoPuesto = true;
      // UN PUNTERO, no un hueco. Sin esta línea la prosa se lee como si le
      // faltara algo justo donde antes había diez fichas.
      salida.push(
        "   Los campos de cada instancia, uno a uno, están en las PESTAÑAS de"
      );
      salida.push(
        "   aquí abajo — con su marca de «ya configurada» y el valor copiable."
      );
      salida.push("");
    }
  }
  return salida.join("\n");
}

/** Placeholders que quedan sin resolver — se listan para que nadie los ejecute sin verlos. */
const PLACEHOLDER_RE = /<[A-Z][A-Z0-9_-]*>/g;

/**
 * ======================= PARTIR EL BLOQUE EN COMANDOS ========================
 *
 * Un `command` de `steps.ts` es UN texto con de todo dentro: párrafos de
 * comentarios, comandos de una línea, comandos de varias con `\`, bucles
 * `for … ; do … ; done` y líneas en blanco. Se pintaba en un solo <pre> y el
 * operador seleccionaba a mano.
 *
 * REGLA DE PARTIDO (elegida para fallar del lado seguro — partir de más es peor
 * que no partir: un comando cortado a la mitad y pegado en una terminal de
 * producción no tiene deshacer):
 *
 *  1. «Un comando» = una línea ejecutable MÁS todo lo que le falta para estar
 *     completa. Sigue abierta mientras: termine en `\`, termine en `|`/`||`/`&&`,
 *     haya un bloque `for/while/until/select/if/case` sin cerrar, o quede una
 *     comilla sin cerrar. El bucle de los 17 schedulers es UN comando, no 19.
 *  2. Una línea que empieza por `#` NUNCA es ejecutable — aunque acabe en `\`
 *     (en el paso 8 hay una `bq query` COMENTADA, con continuación y todo).
 *     Se ve, pero no entra al portapapeles.
 *  3. Los comentarios van ENCIMA del comando que introducen. La única excepción
 *     son las anotaciones de salida esperada, que en este runbook empiezan por
 *     `->` («-> tiene que decir el MISMO proyecto…»): ésas se pegan DEBAJO del
 *     comando al que pertenecen, que es donde el operador las busca.
 *  4. Un comentario al final de la línea (`… | wc -l   # -> 37`) SE QUEDA. Es
 *     shell válido, y quitarlo bien exige un parser de verdad (un `#` puede
 *     estar dentro de comillas). Copiar de más aquí no rompe nada; recortar mal
 *     sí.
 *  5. Si el bloque no se puede cerrar (se acaba el texto con un bucle abierto o
 *     una comilla suelta, o aparece un `done` de más), NO SE PARTE: se devuelve
 *     `null` y la vista pinta el bloque entero, como siempre.
 */
export interface CommandChunk {
  /** Los comentarios que introducen el comando. Se VEN, no se copian. */
  comments: string;
  /** Lo ejecutable — EXACTAMENTE lo que se va al portapapeles. "" = trozo sólo de comentarios. */
  code: string;
  /** La salida esperada («-> …»), pegada debajo. Se ve, no se copia. */
  trailing: string;
}

const COMMENT_LINE = /^\s*#/;
const BLANK_LINE = /^\s*$/;
/** `# -> lo que tienes que ver`. Lo único que se pinta DEBAJO del comando. */
const EXPECTATION_LINE = /^\s*#\s*->/;

const BLOCK_OPENERS = ["for", "while", "until", "select", "if", "case"];
const BLOCK_CLOSERS = ["done", "fi", "esac"];

/** Cuánto abre (o cierra) esta línea en bloques de shell. */
function blockDelta(line: string): number {
  let delta = 0;
  for (const token of line.split(/[\s;()]+/)) {
    if (BLOCK_OPENERS.includes(token)) delta += 1;
    else if (BLOCK_CLOSERS.includes(token)) delta -= 1;
  }
  return delta;
}

/**
 * ¿Quedan comillas abiertas? Recorre el texto como lo haría el shell: dentro de
 * comillas simples no hay escapes, fuera de ellas `\` se come el carácter
 * siguiente (incluido el salto de línea de una continuación).
 */
function quotesBalanced(text: string): boolean {
  let inSingle = false;
  let inDouble = false;
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (c === "\\" && !inSingle) {
      i += 1;
      continue;
    }
    if (c === "'" && !inDouble) inSingle = !inSingle;
    else if (c === '"' && !inSingle) inDouble = !inDouble;
  }
  return !inSingle && !inDouble;
}

/** ¿Esta línea deja el comando a medias? */
function continues(line: string): boolean {
  return /(\\|\||\|\||&&)$/.test(line.replace(/\s+$/, ""));
}

/**
 * Parte un `command` en trozos copiables. `null` = no se puede partir con
 * seguridad; el llamador pinta el bloque entero (regla 5).
 */
export function parseCommandBlock(raw: string): CommandChunk[] | null {
  const lines = raw.replace(/\r\n/g, "\n").split("\n");
  const chunks: CommandChunk[] = [];
  let comments: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (BLANK_LINE.test(line)) {
      // Una línea en blanco DENTRO de un párrafo de comentarios se conserva
      // (separa ideas); la de delante del primero se tira.
      if (comments.length) comments.push("");
      i += 1;
      continue;
    }
    if (COMMENT_LINE.test(line)) {
      comments.push(line);
      i += 1;
      continue;
    }

    // --- una unidad ejecutable ---------------------------------------------
    const code: string[] = [];
    let depth = 0;
    let open = true;
    while (open && i < lines.length) {
      const current = lines[i];
      code.push(current);
      i += 1;
      // Regla 2: un comentario dentro del bloque no lo abre ni lo cierra, y
      // tampoco cuenta para las comillas.
      const effective = COMMENT_LINE.test(current) ? "" : current;
      depth += blockDelta(effective);
      if (depth < 0) return null; // un `done` de más: no me fío, no parto.
      const executable = code.filter((l) => !COMMENT_LINE.test(l)).join("\n");
      open = depth > 0 || !quotesBalanced(executable) || continues(effective);
    }
    if (open) return null; // se acabó el texto con el comando a medias.

    // Regla 3: sólo las anotaciones `-> …` pegadas (sin línea en blanco de por
    // medio) bajan con el comando. Lo demás introduce lo que viene después.
    const trailing: string[] = [];
    if (i < lines.length && EXPECTATION_LINE.test(lines[i])) {
      while (i < lines.length && COMMENT_LINE.test(lines[i])) {
        trailing.push(lines[i]);
        i += 1;
      }
    }

    chunks.push({
      comments: comments.join("\n").replace(/\s+$/, ""),
      code: code.join("\n"),
      trailing: trailing.join("\n"),
    });
    comments = [];
  }

  // Comentarios que cierran el bloque y no introducen nada (el «3) VERIFICAR»
  // del paso 27, por ejemplo). Trozo sin comando: se ve, no tiene botón.
  const tail = comments.join("\n").replace(/\s+$/, "");
  if (tail) chunks.push({ comments: tail, code: "", trailing: "" });

  return chunks;
}

/**
 * Escribe en el portapapeles, o dice que no pudo.
 *
 * `navigator.clipboard` EXIGE contexto seguro (https o localhost). Si no lo hay
 * —o el permiso se deniega— se intenta el `execCommand` viejo, y si tampoco,
 * se devuelve `false` para que la vista lo DIGA: fallar en silencio aquí es que
 * el operador pegue en la terminal lo que copió hace tres pasos.
 */
async function writeClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // sin permiso o sin contexto seguro — cae al fallback
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export default defineComponent({
  name: "MigrationConsole",
  components: {
    DraftPreviewTable,
    MmiDraftPreview,
    InvariantGate,
    StepAuto,
    CatalogCheck,
    CofounderRanks,
  },
  data() {
    return {
      runId: "",
      // BD de escritura del run (#595). TOPOLOGÍA A: `gtr-2026`, y es el MISMO
      // nombre en el go-live y en el ensayo (Hector, 2026-07-21) — así el writeDbId
      // y el env del flip son idénticos en los dos y no queda un string que
      // traducir el sábado. Vacío => `(default)`, que en A es la RED DE ROLLBACK y
      // nunca es un destino válido; por eso el vacío pide confirmación explícita.
      // El índice de runs sigue viviendo en migration-control pase lo que pase.
      writeDbId: "gtr-2026",
      // Confirmación explícita para el caso peligroso (writeDbId vacío => default).
      // No existe un "vacío está bien": o hay BD, o alguien tiene que decir que sí.
      ackDefaultDb: false,
      run: undefined as MigrationRun | undefined,
      // Los runs guardados, para el grid de cards de selección. Se llenan con
      // Migration-ListRuns al montar — la callable existía desde el principio y el
      // front nunca la llamaba, así que la única forma de abrir un run era teclear
      // su nombre exacto (y un typo creaba uno nuevo en silencio).
      runsList: [] as MigrationRun[],
      steps: [] as MigrationStepDef[],
      environment: "",
      // `null` = TODAVÍA NO SE HA PREGUNTADO. No es "abierto". Ver el banner: el
      // valor de fábrica `{ frozen: false }` hacía que cada refresco acusara al
      // sistema de estar abierto sin haber leído la bandera.
      freeze: null as MigrationFreezeState | null,
      drafts: {} as Record<string, MigrationDraft>,
      /**
       * EL INFORME DE CADA GATE, por paso. Son dos —el estructural del 18 y el
       * de descongelar— y los dos se pintan con el mismo componente, así que
       * viven en el mismo sitio indexados por su paso. Que falte la clave
       * significa «de este gate no hay informe», que es justo lo que se quiere
       * decir mientras corre.
       */
      gateReports: {} as Record<string, GateReport>,
      // LO QUE DEVOLVIÓ LA ÚLTIMA LLAMADA DE CADA PASO, dry-run incluido. Vive en
      // la página y no en el run A PROPÓSITO: un dry-run no deja rastro en
      // Firestore (`runStep` se salta markStepRunning/markStepDone), así que si no
      // se guarda aquí no existe en ningún sitio. Se pierde al recargar, como el
      // dry-run mismo.
      lastCall: {} as Record<
        string,
        { dryRun: boolean; label: string; at: string; payload: unknown }
      >,
      // El reporte de puntos del paso 23 (`Migration-PointsReport`). Sólo
      // lectura: tampoco toca el run, así que también vive aquí.
      pointsReport: undefined as PointsReport | undefined,
      /**
       * ¿ESTÁ LA FOTO FIRMADA? Es el interruptor de la pantalla entera.
       *
       * Confirmado ⇒ los botones que escriben (Aplicar / Crear / Borrar) SE
       * OCULTAN, no se deshabilitan: lo que se guardó describe la base tal y como
       * estaba en ese instante, y dejar los botones a la vista invita a
       * invalidarla. `undefined` mientras no se haya corrido el reporte — no es
       * «no confirmado», es «todavía no lo sé», y por eso no pinta nada.
       */
      pointsConfirmation: undefined as ReportConfirmation | undefined,
      /**
       * Los descuadres que se están mirando en el pop-up. `null` = cerrado.
       * Es una LISTA porque se abre de dos sitios: el aviso de arriba (todos) y
       * la insignia de un renglón (los de esa cuenta).
       */
      cuadreVistas: null as CuadreLeg[] | null,
      /** El título del pop-up: «todos» o el nombre de la cuenta. */
      cuadreTitulo: "",
      /**
       * La casilla de RECONOCER los descuadres del árbol viejo. Nace apagada en
       * cada lectura: reconocer es un acto, y un acto no se hereda de la vez
       * anterior.
       */
      ackCuadreViejo: false,
      // La foto del catálogo. `undefined` hasta que alguien pulsa: una tabla de
      // diez «no existe» pintada de entrada, sin haber leído nada, sería una
      // alarma inventada — y las alarmas inventadas enseñan a ignorarlas.
      catalogReport: undefined as CatalogReport | undefined,
      catalogStepId: CATALOG_STEP_ID,
      // El paso que PRODUCE el sello del export, y el único que pinta el campo
      // en su pie. Ver el bloque del template.
      backupStepId: BACKUP_STEP_ID,
      // La foto de los fundadores (rango + porcentaje de binario). `undefined`
      // hasta que alguien pulsa, por lo mismo que el catálogo: una tabla en rojo
      // pintada sin haber leído nada es una alarma inventada.
      cofounderReport: undefined as CofounderReport | undefined,
      // Lo que escribió la ÚLTIMA aplicación. Se limpia en cada lectura: dejarlo
      // colgado haría que un «escrito ahora mismo» de hace media hora se leyera
      // como si acabara de pasar.
      cofounderWrites: [] as CofounderWrite[],
      /**
       * LAS CUENTAS MARCADAS. Arranca VACÍA y vuelve a vaciarse tras aplicar.
       *
       * Nada se aplica por omisión (Hector, 2026-07-26): el operador valida
       * cuenta por cuenta, así que el estado en el que nace la pantalla es «no
       * he decidido nada». Vaciarla después de escribir es la otra mitad — dejar
       * las casillas puestas invita a un segundo clic que ya no significa lo
       * mismo, porque las filas de la tabla han cambiado debajo.
       */
      cofounderSelection: [] as string[],
      cofounderStepId: COFOUNDER_STEP_ID,
      // El estado del IMPORT por cuenta, tal y como lo devuelve la callable —
      // releído de Firestore, nunca lo que creíamos que iba a pasar. Vive aquí y
      // no en el run porque es estado de PANTALLA: al recargar se vuelve a pedir.
      //
      // SE LLENA DE DOS SITIOS, y los dos son la misma fuente de verdad (el
      // documento de bitácora del importador): el reporte lo trae ENTERO al
      // correrse, y cada `Aplicar`/`Borrar` actualiza su propia fila. Antes sólo
      // se llenaba de lo segundo, así que al recargar la consola las filas ya
      // aplicadas volvían a ofrecer «Aplicar» como si nada hubiera pasado.
      importState: {} as Record<string, ImportRowSummary>,
      // EL IMPORTE QUE SE VA A CREAR, por `cuenta:etiqueta`. Nace con el valor
      // PROPUESTO y el operador lo puede cambiar antes de confirmar: la cifra la
      // decide él renglón por renglón, aquí sólo se le ahorra teclearla.
      crearMonto: {} as Record<string, number>,
      open: {} as Record<string, boolean>,
      approved: {} as Record<string, boolean>,
      minutes: {} as Record<string, number>,
      notes: {} as Record<string, string>,
      anchorsJson: "",
      anchorsPlaceholder: ANCHORS_PLACEHOLDER,
      // Los `package` VIVOS, para pintarlos junto al textarea. Ya no es un
      // espejo: es la MISMA constante que consume el backend (`PACKAGE_LABELS`
      // de packageIds.ts, de donde `corte.ts` saca su `ANCHOR_PACKAGES`). La
      // validación que manda sigue siendo la de allá — ésta sólo evita que el
      // operador descubra el dedazo después de pulsar.
      anchorPackages: ANCHOR_PACKAGES,
      // VACÍO A PROPÓSITO, igual que las anclas: los alias dependen del ENTORNO
      // (en prod son tres, en el emulador dos) y un default que sólo acierta en
      // uno de los dos se aprueba sin leerlo. El ejemplo va de placeholder, en
      // gris, y el payload de verdad sale del fichero del entorno. Ver
      // `ALIASES_PLACEHOLDER`.
      aliasesJson: "",
      aliasesPlaceholder: ALIASES_PLACEHOLDER,
      // Pit decidió IMPORTAR (2026-07-18): ambas arrancan en `true` — los sembrados
      // CONSERVAN su calificación binaria y su power line. Siguen siendo toggles
      // EDITABLES (el operador puede cambiarlas antes de aplicar el paso 15).
      copyBinaryExpiration: true as boolean | null,
      copyInnerLegUnlocked: true as boolean | null,
      // EL PROYECTO DE LOS COMANDOS (`<PROJECT>`). Vive en la página, no en el
      // run: es cómo se PINTAN los comandos, no un dato de la migración — el
      // backend nunca lo ve. Arranca en sandbox a propósito.
      projectId: MIGRATION_PROJECTS[0].id,
      projectOptions: MIGRATION_PROJECTS,
      // EL SELLO DEL EXPORT, TAL COMO SE PEGÓ. Crudo a propósito: aquí entra lo
      // que el operador tenga en el portapapeles (el sello suelto o la ruta
      // entera), y de ahí sale el sello limpio en `exportStampValue`. Guardar el
      // crudo es lo que permite decirle «esto que pegaste no lleva sello» en vez
      // de tragárselo en silencio. Se carga y se guarda por runId — ver
      // `readExportStamp`, y el porqué de que no viva en el run.
      exportStamp: "",
      /**
       * QUÉ INSTANCIAS QUEDAN CONFIGURADAS, por paso. Se carga del navegador al
       * abrir el run (ver `readInstancesDone`) y se reescribe en cada tic.
       */
      instancesDone: {} as Record<string, string[]>,
      /**
       * QUÉ PESTAÑA ESTÁ ABIERTA, por paso. Vacío = la primera. No se persiste a
       * propósito: dónde estabas mirando no es trabajo hecho, y tras un refresco
       * lo que importa es ver el conjunto y por dónde vas.
       */
      instanceTab: {} as Record<string, string>,
      // El bucket viejo de prod, sólo para nombrarlo en el aviso. Ver la tabla.
      lookalikeProdBucket: LOOKALIKE_PROD_BUCKET,
      // Qué se acaba de copiar, para dar acuse de recibo en ESE botón y no en
      // todos. Un solo string: sólo se copia una cosa a la vez.
      copiedKey: "",
      copyError: "",
      busy: false,
      busyAction: "",
      error: "",
      // EL PASO AL QUE PERTENECE `error`, para que la barra de arriba lo nombre.
      // "" = el fallo no es de ningún paso (cargar el run, listar los runs).
      errorStepId: "",
      // EL MISMO MENSAJE, INDEXADO POR PASO, para pintarlo DENTRO de su tarjeta.
      // Vive en la página y no en el run a propósito: los pasos `auto` sí dejan
      // su error en `steps[id].note` (markStepFailed), pero un dry-run y las
      // llamadas de sólo lectura (el reporte del 22) no tocan el run — y el
      // fallo de una callable que ni siquiera llegó a `runStep` (orden,
      // argumentos) tampoco. Sin esto esos tres casos no se ven en ninguna parte.
      stepError: {} as Record<string, string>,
    };
  },
  mounted() {
    // Fill the grid on entry. A failure here must not blank the console — it just
    // means no cards, and the operator can still create/open a run by name.
    void this.loadRuns();
  },
  watch: {
    /**
     * El sello se guarda EN CUANTO SE ESCRIBE, no al pulsar nada.
     *
     * No hay botón de guardar a propósito: un botón es una cosa más que se puede
     * olvidar, y lo que se pierde al olvidarlo es exactamente el dato que esto
     * viene a proteger. Se guarda el CRUDO, no el sello limpio, para que tras un
     * refresco el operador vea lo mismo que pegó — incluido el dedazo, con su
     * aviso al lado.
     */
    exportStamp(value: string) {
      writeExportStamp(this.runId, value);
    },
  },
  computed: {
    isProd(): boolean {
      return !!this.environment && !this.environment.includes("sandbox");
    },
    /**
     * El run que se va a CREAR apunta a `(default)` (campo vacío o el literal).
     * En Topología A eso es la red de rollback, y el paso 10 lo prohíbe: es el
     * único caso en el que el formulario pide una confirmación aparte.
     */
    targetsDefaultDb(): boolean {
      const id = this.writeDbId.trim();
      return id === "" || id === "(default)";
    },
    /** Crear sólo cuando la BD está elegida, o cuando el `(default)` es a sabiendas. */
    canCreate(): boolean {
      return !this.targetsDefaultDb || this.ackDefaultDb;
    },
    /**
     * ¿El proyecto elegido para los comandos es PRODUCCIÓN? Es el interruptor
     * del rojo: selector, franja, borde de cada bloque y color de cada botón de
     * copiar cuelgan de aquí. No se deduce del nombre — lo declara la tabla.
     */
    projectIsProd(): boolean {
      return !!MIGRATION_PROJECTS.find((p) => p.id === this.projectId)?.prod;
    },
    /**
     * El bucket de backups del proyecto elegido — el `<BUCKET>` de los comandos.
     * Cuelga del MISMO selector que el proyecto porque es un dato suyo: elegir
     * sandbox arriba y dejar el bucket de producción abajo es una combinación
     * que no existe, y hasta hoy era una que se podía teclear.
     */
    projectBucket(): string {
      return (
        MIGRATION_PROJECTS.find((p) => p.id === this.projectId)?.bucket ?? ""
      );
    },
    /**
     * EL SELLO YA LIMPIO, o "" mientras no haya uno. Es lo único que se
     * sustituye en los comandos: el crudo se queda en el campo.
     */
    exportStampValue(): string {
      return normalizeExportStamp(this.exportStamp);
    },
    /**
     * Se ha pegado algo, pero ahí dentro no hay un sello. No es lo mismo que
     * vacío y no se puede tratar igual: el campo vacío es «todavía no toca», y
     * esto es «creíste que lo habías puesto». Decirlo con lo que se pegó delante
     * es lo que hace que el operador vea el dedazo.
     */
    exportStampProblem(): string {
      if (!this.exportStamp.trim() || this.exportStampValue) return "";
      return (
        `Lo pegado no lleva un sello con la forma AAAAMMDD-HHMM (ocho dígitos, ` +
        `guion, cuatro): «${this.exportStamp.trim()}». Pega la ruta completa que ` +
        `devolvió el export, o sólo el sello — de la ruta se saca solo.`
      );
    },
    /**
     * LA RUTA A LA QUE VA A APUNTAR EL IMPORT, entera y a la vista. El operador
     * la compara de un vistazo con la que tiene en la terminal, que es la única
     * comprobación real que existe: `gcloud` no valida que la carpeta exista.
     */
    exportPath(): string {
      if (!this.exportStampValue || !this.projectBucket) return "";
      return `gs://${this.projectBucket}/pre-reset-${this.exportStampValue}`;
    },
    /**
     * La consola corre en un proyecto (`environment`, que es el GCLOUD_PROJECT
     * de las functions) y los comandos se pintan para OTRO. Es legítimo mientras
     * se prepara, y es un desastre a la hora de ejecutar: el default de esta
     * página es sandbox, así que el día del go-live —consola en gtr-academy—
     * esto es exactamente el recordatorio de cambiar el selector.
     */
    projectMismatch(): string {
      const env = this.environment;
      if (!env || env === "unknown" || env === this.projectId) return "";
      return (
        `Esta consola corre sobre ${env} y los comandos se están pintando para ` +
        `${this.projectId}. Uno de los dos está mal: o cambias el selector, o ` +
        `estás en la consola equivocada.`
      );
    },
    doneCount(): number {
      const steps = this.run?.steps ?? {};
      return Object.values(steps).filter(
        (s) => s.status === "done" || s.status === "skipped"
      ).length;
    },
    /**
     * El instante del run en ISO 8601 CON milisegundos — el formato que come
     * `new Date(...)` en el barrido del paso 10. `toISOString()` los conserva; un
     * formato "bonito" los perdería y el gate del paso 18 se pondría rojo para
     * todos los users por 800 milisegundos de diferencia.
     */
    migrationDateIso(): string {
      if (!this.run?.migrationDate) return "—";
      return new Date(this.run.migrationDate).toISOString();
    },
    /** Los alias parseados, o por qué no parsean. */
    parsedAliases(): { map?: Record<string, string>; error?: string } {
      const raw = this.aliasesJson.trim();
      // Vacío NO es `{}`. Mandar `{}` es afirmar "no hay alias", y esa afirmación
      // ya no la discute nadie: desde el 2026-07-26 el backend no lleva lista con
      // la que contrastarla, así que `{}` se APLICA — `pflores` recibiría nodo
      // propio y su línea quedaría partida. Este campo vacío se para aquí porque
      // aquí es donde queda alguien mirando.
      if (!raw) return { error: "Los alias son obligatorios. Mínimo: {}." };
      try {
        const parsed = JSON.parse(raw);
        if (
          typeof parsed !== "object" ||
          parsed === null ||
          Array.isArray(parsed)
        ) {
          return { error: "Debe ser un objeto JSON { alterna: canónica }." };
        }
        for (const [k, v] of Object.entries(parsed)) {
          if (typeof v !== "string") {
            return { error: `"${k}" apunta a algo que no es un username.` };
          }
        }
        return { map: parsed as Record<string, string> };
      } catch (e) {
        return { error: `JSON inválido: ${(e as Error).message}` };
      }
    },
    aliasesError(): string | undefined {
      return this.parsedAliases.error;
    },
    aliasesCount(): number {
      return Object.keys(this.parsedAliases.map ?? {}).length;
    },
    /** Parsed anchors, or the reason they don't parse. */
    parsedAnchors(): { list?: unknown[]; error?: string } {
      const raw = this.anchorsJson.trim();
      if (!raw) return {};
      try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return { error: "Debe ser un array JSON." };
        return { list: parsed };
      } catch (e) {
        return { error: `JSON inválido: ${(e as Error).message}` };
      }
    },
    anchorsError(): string | undefined {
      return this.parsedAnchors.error;
    },
    anchorsCount(): number {
      return this.parsedAnchors.list?.length ?? 0;
    },
    /**
     * Anclas cuyo `package` no existe — `usuario (valor)`, para nombrarlas antes
     * de pulsar. El backend las rechaza igual (`assertAnchorPackages`), que es
     * donde está la garantía; esto es el aviso temprano, no la puerta.
     */
    anchorsBadPackages(): string[] {
      const list = this.parsedAnchors.list ?? [];
      return list
        .filter((a) => {
          const pkg = (a as { package?: unknown }).package;
          // `isPackageLabel`, NO `ANCHOR_PACKAGES.includes`: acepta las dos
          // generaciones, igual que `isAnchorPackage` en el backend. Con la
          // lista a secas, un payload de anclas escrito con `gtr-basic` salía
          // aquí en rojo y en el backend en verde — la consola mintiendo.
          // Y sin guard, a propósito: se pregunta por CADA ancla para nombrarlas
          // todas de golpe, así que la primera mala no puede reventar.
          return !isPackageLabel(pkg);
        })
        .map((a) => {
          const anchor = a as { username?: unknown; package?: unknown };
          return `${String(anchor.username ?? "?")} (${JSON.stringify(
            anchor.package
          )})`;
        });
    },
    flagsChosen(): boolean {
      return (
        typeof this.copyBinaryExpiration === "boolean" &&
        typeof this.copyInnerLegUnlocked === "boolean"
      );
    },
    // Aquí vivía `reportStepId`, que pintaba el NÚMERO del paso del reporte para
    // que la tarjeta que lo consumía pudiera decir «la salida del paso 23». Esa
    // tarjeta era la del id restore-binary-points--c y ya no existe: sin otro sitio
    // desde el que nombrarlo, la frase no tiene a quién servir.
    /**
     * ¿Se puede pulsar «Correr el reporte»? Se mira SIEMPRE el paso del reporte.
     *
     * `unlocked`, NO `isDone`: correr el reporte sólo lee y está pensado para
     * re-correrse después de cada fila restituida — marcarlo «Hecho» no apaga el
     * botón, que es como se comprueba que lo importado está de verdad escrito.
     */
    reportUnlocked(): boolean {
      const step = this.steps.find((s) => s.id === REPORT_STEP_ID);
      return !!step && stepIsUnlocked(step, this.run);
    },
    /**
     * ¿Se puede LEER la tabla de los fundadores? `unlocked`, no `isDone`: leer
     * es gratis y se re-corre después de cada ajuste, igual que el catálogo.
     */
    cofounderUnlocked(): boolean {
      const step = this.steps.find((s) => s.id === COFOUNDER_STEP_ID);
      return !!step && stepIsUnlocked(step, this.run);
    },
    /**
     * ¿POR QUÉ ESTÁ APAGADO «Aplicar» en los fundadores? Devuelve el motivo, o
     * `""` si se puede pulsar.
     *
     * Un botón gris que no dice por qué se lee como una avería, y aquí el
     * apagado tiene motivos distintos que piden cosas distintas: leer primero,
     * marcar algo, o que ya no quede nada que escribir.
     *
     * EL ORDEN IMPORTA y no es alfabético. «No has marcado nada» va ANTES que
     * «ya está»: si el operador no ha marcado, lo que necesita oír es que le
     * toca elegir, no un parte del estado del mundo. Y «ya está» va DESPUÉS de
     * comprobar si queda algo pendiente, porque con una cuenta bloqueada y las
     * demás hechas las dos frases son ciertas a la vez y la que sirve es la que
     * dice qué hacer a continuación.
     */
    cofounderApplyDisabledReason(): string {
      if (!this.cofounderReport)
        return "Lee primero la tabla: el botón escribe sobre lo que se ve, no a ciegas.";
      if (!this.cofounderSelection.length)
        return (
          "No has marcado ninguna cuenta. Este paso escribe SÓLO sobre lo que " +
          "marques — nada se aplica por omisión."
        );
      const v = this.cofounderReport.veredicto;
      // Lo que este clic escribiría DE VERDAD: lo marcado que además está
      // pendiente. Marcar una fila ya completa no es un error —es idempotente—
      // pero si TODO lo marcado ya está hecho, el botón no tiene trabajo y hay
      // que decirlo en vez de dejar pulsar algo que no hace nada.
      const pendientesMarcadas = this.cofounderSelection.filter((u) =>
        v.pendientes.includes(u)
      );
      if (pendientesMarcadas.length) return "";
      return (
        `Lo que has marcado ya está escrito en esta base ` +
        `(${this.cofounderReport.databaseId}): ` +
        `\`${this.cofounderReport.rankId}\` y ` +
        `${this.cofounderReport.percentage}% en el nodo. Volver a pulsar no ` +
        `escribiría nada.` +
        (v.bloqueadas.length
          ? ` Siguen sin resolver ${v.bloqueadas.join(" · ")}, pero les falta` +
            ` el documento: eso se arregla en la construcción del árbol, no aquí.`
          : "")
      );
    },
    gateGreen(): boolean {
      const gate = this.run?.steps?.["gate--c"];
      const result = gate?.result as { ok?: boolean } | undefined;
      return gate?.status === "done" && result?.ok === true;
    },
    /** La frase del dedazo de `package`, para el paso 15. */
    badPackagesReason(): string {
      return (
        `package inválido en ${this.anchorsBadPackages.length} ancla(s): ` +
        `${this.anchorsBadPackages.join(" · ")}. Válidos: ` +
        `${ANCHOR_PACKAGES.join(" | ")} (las etiquetas retiradas —gtr-basic, ` +
        `gtr-lite, gtr-vip— también se aceptan). Ojo: "trading-academy" es el id ` +
        `de la SUSCRIPCIÓN, no un nivel; el nivel que querías es "gtr-fly", que ` +
        `se escribe igual que el doc-id nuevo de esa familia y NO es lo mismo.`
      );
    },
  },
  methods: {
    /**
     * 🔴 EL `timeout` NO ES OPCIONAL AQUÍ, Y NO ES EL DEL SERVIDOR.
     *
     * Son DOS relojes distintos. El de la función está en 540 s
     * (`MigrationFuncs.ts`, `RUNTIME.timeoutSeconds`), pero el SDK de Firebase
     * trae el SUYO en el cliente y vale **70 segundos** por defecto — y esa es la
     * cifra que manda sobre lo que ve el operador.
     *
     * Los pasos de esta consola son justo los que pasan de ahí: el gate carga
     * ~41.6k usuarios, y el volcado de la cuenta de empresa recorre su subárbol
     * entero en las DOS bases. Con el default, a los 70 s la pantalla pinta
     * `deadline-exceeded` — que parece un fallo del PASO y no lo es.
     *
     * Y lo de verdad peligroso no es el mensaje: **el servidor no se entera de
     * que el cliente se rindió y sigue trabajando hasta terminar**. En un
     * dry-run da igual, no escribe; en un Ejecutar, la consola diría «falló»
     * mientras la escritura se completa por detrás, y el operador volvería a
     * pulsar encima de una operación viva. Por eso el reloj del cliente va
     * atado al del servidor, no por debajo.
     */
    async call<T>(name: string, data: unknown): Promise<T> {
      const fn = fb.func.httpsCallable(`Migration-${name}`, {
        timeout: CALLABLE_TIMEOUT_MS,
      });
      const response = await fn(data);
      if (response.data.OPCODE !== "SUCCESS") {
        throw new Error(
          response.data.message || "La callable devolvió un error."
        );
      }
      return response.data.data as T;
    },
    /**
     * `busy` deshabilita TODOS los botones; `busyAction` dice CUÁL se pulsó.
     *
     * Sin lo segundo, un spinner por botón serían quince girando a la vez y el
     * operador no sabría cuál es la operación que está esperando. Y aquí importa
     * más de lo normal: estos pasos tardan minutos (el gate carga ~41.6k users),
     * así que un botón sólo gris deja al operador sin saber si pulsó bien, si se
     * colgó, o si debe volver a pulsar — y volver a pulsar es justo lo que no
     * queremos en la ventana congelada.
     *
     * La clave la pone el llamador y suele llevar el id del paso, porque el
     * mismo verbo existe en varios pasos a la vez (`resolve:12` vs `resolve:13`).
     */
    async guarded(action: string, work: () => Promise<void>): Promise<void> {
      this.busy = true;
      this.busyAction = action;
      this.error = "";
      this.errorStepId = "";
      try {
        await work();
      } catch (e) {
        this.error = (e as Error).message;
      } finally {
        this.busy = false;
        this.busyAction = "";
      }
    },
    /**
     * `guarded`, pero ATRIBUIDO A UN PASO.
     *
     * Todo lo que se lanza desde la tarjeta de un paso pasa por aquí, y el
     * mensaje acaba en TRES sitios: la barra de arriba (con el número del paso),
     * la tarjeta del paso, y —cuando la callable llegó a `runStep`— el propio run
     * (`note`). Por eso se recarga el run en el `catch`: `markStepFailed` deja el
     * paso en rojo con su nota, y sin recargar la consola seguía enseñándolo
     * «pendiente» hasta que alguien pulsara «Recargar».
     *
     * La recarga va en su propio try: si TAMBIÉN falla, el que manda es el error
     * original — el del paso — no el de la recarga.
     *
     * `action` es la clave del SPINNER (ver `guarded`): el paso dice DÓNDE se
     * pinta el error, la acción dice QUÉ botón está girando. Son dos cosas
     * distintas y hacen falta las dos — un paso tiene varios botones (resolver /
     * simular / aplicar) y sin la acción girarían los tres a la vez.
     */
    async guardedStep(
      stepId: string,
      action: string,
      work: () => Promise<void>
    ): Promise<void> {
      this.busy = true;
      this.busyAction = action;
      this.error = "";
      this.errorStepId = "";
      // Limpia el intento anterior de ESTE paso: un mensaje viejo junto a un paso
      // que acaba de correr bien se lee como «volvió a fallar».
      this.stepError = { ...this.stepError, [stepId]: "" };
      // ══ Y EL RESULTADO ANTERIOR TAMBIÉN ═══════════════════════════════════
      // Hector, 2026-07-26, corriendo el ensayo: «al volverle a dar correr,
      // borrar esa lista, porque ahorita estaba corriendo y los errores pasados
      // estaban aún ahí».
      //
      // Estos pasos tardan minutos —el gate lee la base entera—, y durante todo
      // ese rato la tarjeta seguía enseñando el informe de la corrida ANTERIOR
      // con su cabecera verde o roja. No es "información de más": es un
      // veredicto caducado, sobre unos datos que a lo mejor ya se arreglaron,
      // pintado exactamente igual que uno recién salido. Se borra AQUÍ, en el
      // único sitio por el que pasan todas las acciones de un paso, para que no
      // haya que acordarse botón a botón.
      this.lastCall = sinLaClave(this.lastCall, stepId);
      if (this.isGateStep(stepId)) {
        this.gateReports = sinLaClave(this.gateReports, stepId);
      }
      try {
        await work();
      } catch (e) {
        const message =
          (e as Error).message || "La callable falló sin mensaje.";
        this.error = message;
        this.errorStepId = stepId;
        this.stepError = { ...this.stepError, [stepId]: message };
        // La tarjeta plegada esconde el diagnóstico que acabamos de escribir.
        this.open = { ...this.open, [stepId]: true };
        try {
          await this.load();
        } catch {
          // El run no se pudo recargar. El error del PASO es el que importa.
        }
      } finally {
        this.busy = false;
        this.busyAction = "";
      }
    },
    /** ¿Es ESTE botón el que disparó la operación en curso? */
    isRunning(action: string): boolean {
      return this.busy && this.busyAction === action;
    },
    /**
     * La clave de acción de un paso automático, en UN SOLO SITIO.
     *
     * La arman dos consumidores —quien lanza la llamada y quien decide si ese
     * botón enseña su spinner—, y si cada uno se la construye por su cuenta,
     * basta con que uno cambie para que el otro deje de reconocerla. No
     * fallaría: simplemente el spinner no saldría nunca, que es un fallo mudo.
     */
    autoAction(stepId: string, dryRun: boolean): string {
      return `auto:${stepId}:${dryRun ? "dry" : "run"}`;
    },
    /**
     * La clave de acción de un paso manual — igual que `autoAction`, y por lo
     * mismo: la arman quien lanza la llamada y quien pinta el spinner.
     *
     * LA VARIANTE VA EN LA CLAVE. «Hecho» y «N/A — saltado» son el mismo método
     * (`completeManual`) con distinto argumento, y los dos botones están uno al
     * lado del otro. Con una clave común —`manual:${stepId}`— pulsar uno haría
     * girar LOS DOS, que es peor que no tener indicador: dice que está corriendo
     * algo que no se pulsó.
     */
    manualAction(stepId: string, skipped: boolean): string {
      return `manual:${stepId}:${skipped ? "skip" : "done"}`;
    },
    /**
     * Abrir (o crear) un run.
     *
     * `writeDbId` VIAJA COMO ARGUMENTO, no se lee del input. La creación manda el
     * campo del formulario; la tarjeta manda EL DE LA TARJETA. Antes lo tomaba
     * siempre del input, y por eso reabrir un run existente desde su tarjeta daba
     * un 400: `createRun` es idempotente sólo si el destino COINCIDE, y desde la
     * tarjeta llegaba vacío ⇒ "ya existe con writeDbId=gtr-2026; no se puede
     * reabrir apuntando a (default)". Los únicos runs reabribles por tarjeta eran
     * los que apuntaban a `(default)` — justo la configuración prohibida en A.
     */
    openRun(writeDbId?: string): Promise<void> {
      return this.guarded("openRun", async () => {
        // Switching runs: drop everything held from the previous one. `load()`
        // rebuilds run/steps/drafts/gate, but the local approval checkboxes are
        // ours — an "already approved" tick carried across runs would let you apply
        // a draft you never looked at. Idem los resultados de la última llamada y
        // el reporte del paso 23: son de OTRO run.
        this.approved = {};
        this.gateReports = {};
        this.lastCall = {};
        this.pointsReport = undefined;
        // La foto del catálogo es DE ESTE RUN y de SU base: arrastrarla a otro
        // run enseñaría el catálogo de una base que no es la que se va a leer.
        this.catalogReport = undefined;
        // Idem la de los fundadores: el rango y el porcentaje son de LA BASE de
        // este run, y enseñar los de otro es enseñar un verde que no es tuyo.
        this.cofounderReport = undefined;
        this.cofounderWrites = [];
        this.cofounderSelection = [];
        this.stepError = {};
        // El sello del export es DE ESTE RUN: se recupera del navegador por
        // runId. Aquí es donde sobrevive al refresco — tras recargar la página
        // la consola vuelve a la rejilla, y abrir la tarjeta del run pasa por
        // esta línea. Si no hay nada guardado queda vacío, que es lo correcto:
        // heredar el sello del run anterior sería apuntar el import a otro sitio.
        this.exportStamp = readExportStamp(this.runId);
        const target = (writeDbId ?? this.writeDbId ?? "").trim();
        await this.call("OpenRun", {
          runId: this.runId,
          ...(target ? { writeDbId: target } : {}),
        });
        await this.load();
        // A newly created run should show up in the grid the next time you close
        // back to it.
        await this.loadRunsSilent();
      });
    },
    /**
     * Abrir un run EXISTENTE pulsando su tarjeta — sin teclear, sin typos.
     *
     * Arrastra el `writeDbId` DE LA TARJETA: `createRun` devuelve el run intacto
     * sólo cuando el destino que se le manda es el que el run ya tiene (no toca
     * `migrationDate`); con otro destino RECHAZA, que es lo correcto — el campo es
     * inmutable — pero desde aquí ese rechazo no era un aviso, era un bug.
     */
    selectRun(runId: string): Promise<void> {
      this.runId = runId;
      const card = this.runsList.find((r) => r.runId === runId);
      return this.openRun(card?.writeDbId ?? "");
    },
    /** Back to the grid to pick (or create) another run. Drops the local view
     *  state; the run itself lives in Firestore and is re-openable by its card. */
    closeRun(): Promise<void> {
      return this.guarded("closeRun", async () => {
        // El runId se borra ANTES que el sello a propósito: el watcher que
        // persiste no escribe sin runId, así que vaciar el campo aquí limpia la
        // pantalla sin borrar lo guardado. Volver a abrir el run lo recupera.
        this.runId = "";
        this.exportStamp = "";
        this.run = undefined;
        this.steps = [];
        this.drafts = {};
        this.gateReports = {};
        this.approved = {};
        this.open = {};
        this.lastCall = {};
        this.pointsReport = undefined;
        // La foto del catálogo es DE ESTE RUN y de SU base: arrastrarla a otro
        // run enseñaría el catálogo de una base que no es la que se va a leer.
        this.catalogReport = undefined;
        this.cofounderReport = undefined;
        this.cofounderWrites = [];
        this.cofounderSelection = [];
        this.stepError = {};
        await this.loadRuns();
      });
    },
    /** Fill the card grid from Migration-ListRuns. */
    loadRuns(): Promise<void> {
      return this.guarded("loadRuns", () => this.loadRunsSilent());
    },
    async loadRunsSilent(): Promise<void> {
      const payload = await this.call<ListRunsPayload>("ListRuns", {});
      this.runsList = payload.runs;
      // El freeze viaja con la rejilla para que el banner sepa la verdad SIN un
      // run abierto. Es lo único que se lee aquí que no es de los runs.
      this.freeze = payload.freeze;
    },
    /** Steps a run has finished — for the card badge. Counts skipped as done, the
     *  same rule the progress header uses. */
    runDoneCount(r: MigrationRun): number {
      return Object.values(r.steps ?? {}).filter(
        (s) => s.status === "done" || s.status === "skipped"
      ).length;
    },
    runStatusClass(status: string): string {
      if (status === "open") return "bg-success";
      if (status === "aborted") return "bg-danger";
      if (status === "completed") return "bg-primary";
      return "bg-secondary";
    },
    refresh(): Promise<void> {
      return this.guarded("refresh", () => this.load());
    },
    async load(): Promise<void> {
      const payload = await this.call<GetRunPayload>("GetRun", {
        runId: this.runId,
      });
      this.run = payload.run;
      this.steps = payload.steps;
      this.environment = payload.environment;
      this.freeze = payload.freeze;

      const drafts: Record<string, MigrationDraft> = {};
      if (payload.drafts?.tree)
        drafts["build-trees--c"] = JSON.parse(payload.drafts.tree.json);
      if (payload.drafts?.mmi)
        drafts["build-cmmi--c"] = JSON.parse(payload.drafts.mmi.json);
      this.drafts = drafts;

      // ALWAYS reassign — never "keep the old one if the new run has none".
      //
      // The semáforo is the single most consequential thing on this page. Leaving a
      // stale report on screen means you switch to a fresh run, see a GREEN gate
      // from a DIFFERENT run, and reach for "Descongelar". (The callable would
      // still refuse — the gate is server-side — but a console that shows you green
      // when you are red has already failed at its one job.)
      //
      // LOS DOS GATES, no sólo el del 18: el de descongelar guarda un informe de
      // la misma forma y hasta ahora sólo se veía como JSON en crudo.
      //
      // ⚠️ SALVO QUE EL DE LA PANTALLA SEA MÁS NUEVO. Un DRY-RUN del gate lee la
      // base entera y no toca el run —ése es su trato—, así que su informe sólo
      // existe aquí; recargar acto seguido lo tiraba y ponía en su sitio el de la
      // ÚLTIMA ejecución de verdad, que puede ser de hace horas. O sea: pulsabas
      // «Dry-run», esperabas el minuto, y la pantalla te contestaba con el
      // veredicto viejo — el mismo engaño que este trabajo viene a quitar. Se
      // comparan por `ranAt`, que lo pone el backend al empezar a leer.
      //
      // No hay riesgo de arrastrar el informe de OTRO run: cambiar de run pasa
      // por `openRun`/`closeRun`, y los dos vacían esto antes de recargar.
      const informes: Record<string, GateReport> = {};
      for (const stepId of GATE_STEPS) {
        const guardado = this.run?.steps?.[stepId]?.result as
          | GateReport
          | undefined;
        const enPantalla = this.gateReports[stepId];
        if (
          enPantalla &&
          (!guardado?.report || enPantalla.ranAt > guardado.ranAt)
        ) {
          informes[stepId] = enPantalla;
        } else if (guardado?.report) {
          informes[stepId] = guardado;
        }
      }
      this.gateReports = informes;

      // LAS MARCAS DE LOS FORMULARIOS, del navegador. Aquí y no en un accesor
      // perezoso: leer `localStorage` desde algo que pinta la plantilla se
      // ejecuta en cada repintado, y si además cachea, escribe estado durante el
      // render. Aquí se hace UNA vez por carga, que es cuando los pasos se
      // conocen — `load()` corre al abrir el run y después de cada acción.
      const marcas: Record<string, string[]> = {};
      for (const step of this.steps) {
        if (step.instances?.length) {
          marcas[step.id] = readInstancesDone(this.runId, step.id);
        }
      }
      this.instancesDone = marcas;
    },

    stateOf(id: string): MigrationStepState | undefined {
      return this.run?.steps?.[id];
    },
    statusOf(id: string): MigrationStepStatus {
      return this.stateOf(id)?.status ?? "pending";
    },
    isDone(id: string): boolean {
      const s = this.statusOf(id);
      return s === "done" || s === "skipped";
    },
    unlocked(step: MigrationStepDef): boolean {
      return stepIsUnlocked(step, this.run);
    },
    draftFor(id: string): MigrationDraft | undefined {
      return this.drafts[id];
    },
    toggle(id: string): void {
      this.open = { ...this.open, [id]: !this.open[id] };
    },
    /**
     * CERRAR DESDE EL PIE Y VOLVER A LA CABECERA (Hector, 2026-07-26).
     *
     * El botón del pie de la tarjeta ya cerraba, pero la vista se quedaba donde
     * estaba: la tarjeta se encoge bajo los pies del operador y el paso
     * siguiente queda muy por encima, así que había que subir a mano justo
     * después de cerrar. Aquí se cierra Y se lleva la vista a la cabecera de
     * ESTA tarjeta, que es donde empieza el paso siguiente.
     *
     * SÓLO desde el pie. La cabecera sigue llamando a `toggle` a secas: allí el
     * operador ya está mirando la cabecera y moverle la página sería un salto
     * sin motivo.
     *
     * EL MOMENTO ES LO DELICADO: hay que medir DESPUÉS de que la tarjeta se
     * haya encogido, o la cuenta sale sobre la altura vieja y la vista se queda
     * corta. Por eso van dos esperas encadenadas y no una:
     *
     *   `$nextTick`  espera a que Vue haya aplicado el `v-show` del cuerpo — el
     *                DOM aún es el de antes cuando este método termina.
     *   `rAF`        espera además a que el navegador cierre un ciclo de
     *                pintado: al encoger la tarjeta baja el alto del documento
     *                y el propio navegador recoloca el scroll (lo recorta si
     *                estabas cerca del final, y el anclaje de scroll puede
     *                moverlo). Medir antes de eso es medir un mundo que ya no
     *                existe.
     *
     * Nada de esto lo hace un `setTimeout` con un número inventado: los dos
     * pasos son los dos hechos que hay que esperar, ni más ni menos.
     */
    collapseFromFooter(id: string, ev: Event): void {
      // La tarjeta se resuelve AHORA, no dentro del `$nextTick`: en cuanto
      // termina el despacho del evento, `currentTarget` vuelve a valer null.
      const card = (ev.currentTarget as HTMLElement | null)?.closest(".step");
      const header = card?.querySelector(".step-header");

      this.toggle(id);
      // Sin cabecera que enseñar no hay a dónde ir, pero el plegado YA está
      // hecho: esto sólo renuncia al extra, nunca al cierre.
      if (!(header instanceof HTMLElement)) return;

      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          const rect = header.getBoundingClientRect();

          // El aire que queda por encima vive en el CSS (`scroll-margin-top` de
          // `.step-header`): el navegador ya lo respeta al desplazar, y aquí se
          // LEE para que la comprobación de «ya se ve» use exactamente el mismo
          // número. Si algún día hay una barra superior fija o pegajosa, se
          // suma su alto en el CSS y este código se entera solo. Hoy no la hay
          // —el topbar del dashboard va en el flujo y hace scroll con la
          // página—, así que ese margen es sólo un respiro visual.
          const margin = parseFloat(
            window.getComputedStyle(header).scrollMarginTop
          );
          const safeTop = Number.isFinite(margin) ? margin : 0;
          const viewport =
            window.innerHeight || document.documentElement.clientHeight;

          // Si la cabecera ya se ve ENTERA, no se toca nada: mover la página
          // cuando el operador ya está mirando el sitio es peor que no moverla.
          if (rect.top >= safeTop && rect.bottom <= viewport) return;

          // `prefers-reduced-motion`: sin animación. Con `behavior: "auto"`
          // manda el `scroll-behavior` del CSS, y el de Bootstrap sólo vale
          // dentro de `(prefers-reduced-motion: no-preference)` — es decir,
          // instantáneo justo cuando toca serlo.
          const reduced = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
          ).matches;
          header.scrollIntoView({
            behavior: reduced ? "auto" : "smooth",
            block: "start",
          });
        });
      });
    },
    /**
     * EL NÚMERO DE UN PASO, para nombrarlo en una frase. Sale del `label` que
     * manda el backend (posición dentro de su fase); nunca se calcula aquí.
     *
     * Un id que no está en la lista se devuelve tal cual: esto sólo pinta texto,
     * y un mensaje que revienta mientras explica otro problema es peor que un
     * mensaje con un id raro dentro.
     */
    labelOf(id: string): string {
      return this.steps.find((s) => s.id === id)?.label ?? id;
    },
    /** Los números de los pasos que bloquean a éste, para la frase del candado. */
    requiresLabels(step: MigrationStepDef): string[] {
      return (step.requires || []).map((r) => this.labelOf(r));
    },
    /**
     * La prosa de un paso, formateada. SÓLO `detail` y `warning`.
     *
     * 🔴 NUNCA se le pasa un `command`: ésos salen tal cual, en su bloque
     * monoespaciado, para poder copiarlos carácter por carácter. Formatear un
     * `gcloud` es romper el paso.
     */
    prose(text: string | undefined): string {
      return renderStepProse(text);
    },
    /**
     * EL `detail` TAL Y COMO LO PINTA LA CONSOLA. Idéntico al del paso salvo en
     * los pasos con pestañas, donde se le quitan las fichas de instancia: ahí
     * son la misma información dos veces y la de texto es la que hace el muro.
     * Ver `stripInstanceFiches`.
     */
    detailForConsole(step: MigrationStepDef): string {
      if (!step.instances?.length) return step.detail;
      return stripInstanceFiches(step.detail);
    },
    /** ¿Este paso pide anclas + alias? Sólo el que siembra el árbol. */
    usesAnchors(id: string): boolean {
      return STEPS_WITH_ANCHORS.includes(id);
    },
    /**
     * ¿Esta tarjeta pinta el reporte de puntos? Hoy UNA sola.
     *
     * Eran dos —la del reporte y la de la captura a mano— y el bloque se montaba
     * en las dos para no duplicar la tabla. Desde que el paso de la captura se
     * retiró (2026-07-26) y la restitución se hace desde esta misma tabla, sólo
     * queda un sitio. El método se conserva en vez de inlinar la comparación:
     * es el único punto donde se decide, y si mañana vuelve a hacer falta un
     * segundo sitio se añade aquí y no en la plantilla.
     */
    showsPointsReport(id: string): boolean {
      return id === REPORT_STEP_ID;
    },

    // -- por qué está gris (FIX 6) -------------------------------------------
    //
    // Un botón deshabilitado que no dice su motivo obliga a descubrirlo por
    // prueba y error, y a las 2am eso es tiempo de reloj congelado. Cada razón se
    // devuelve como frase, no como booleano.

    /** Lo que le falta a «1. Calcular draft», o `""` si no le falta nada. */
    resolveBlocker(step: MigrationStepDef): string {
      if (!this.unlocked(step)) {
        // NÚMEROS, no ids: el operador está mirando la pantalla, y ahí los pasos
        // son «11», «12» — no `prune-canonical--c`.
        return `Bloqueado: falta(n) el/los paso(s) ${(step.requires || [])
          .map((r) => this.labelOf(r))
          .join(", ")}.`;
      }
      // ⚠️ ESTA COMPARACIÓN ESTUVO ROTA (2026-07-24): decía `"14"`, el id que
      // este paso tenía ANTES de que entrara «sacar a los Lite del binario». Con
      // el número viejo, el paso de los árboles perdía TODAS sus explicaciones de
      // «por qué está gris» — las anclas, los alias, las dos banderas M2 — y el
      // botón se quedaba apagado sin decir por qué.
      if (step.id !== "build-trees--c") return "";
      if (this.anchorsError)
        return `Las anclas no parsean: ${this.anchorsError}`;
      if (!this.anchorsCount)
        return "Faltan las ANCLAS: sin ellas el árbol sale distinto y en silencio.";
      if (this.anchorsBadPackages.length) return this.badPackagesReason;
      if (this.aliasesError)
        return `Los alias no parsean: ${this.aliasesError}`;
      if (!this.flagsChosen)
        return "Faltan las dos banderas M2 (la callable las exige explícitas).";
      return "";
    },
    /** Las razones de los TRES botones del ciclo de revisión, en orden. */
    reviewBlockers(step: MigrationStepDef): string[] {
      const reasons: string[] = [];
      const resolveWhy = this.resolveBlocker(step);
      if (resolveWhy) reasons.push(`1. Calcular draft — ${resolveWhy}`);
      if (!this.draftFor(step.id)) {
        reasons.push(
          "2. Dry-run y 4. Aplicar — todavía no hay draft calculado en este run."
        );
      } else if (!this.approved[step.id]) {
        reasons.push(
          "4. Aplicar — falta marcar «3. Revisé el árbol y la tabla, y lo " +
            "apruebo»."
        );
      }
      return reasons;
    },

    // -- lo que devolvió la última llamada (FIX 3) ---------------------------

    /**
     * Guarda el resultado de una llamada para PINTARLO. Un dry-run no deja rastro
     * en el run a propósito, así que sin esto no se ve en ninguna parte.
     */
    recordCall(
      stepId: string,
      dryRun: boolean,
      label: string,
      payload: unknown
    ): void {
      this.lastCall = {
        ...this.lastCall,
        [stepId]: {
          dryRun,
          label,
          at: new Date().toLocaleTimeString(),
          payload,
        },
      };
    },
    /**
     * Avisos que se leen del propio resultado — SOLO sobre datos que vienen
     * estructurados. No se inventa la comparación contra el "LO QUE DEBES VER" de
     * cada paso: lo que no viene en el payload, no se afirma.
     */
    resultFlags(stepId: string): string[] {
      const payload = this.lastCall[stepId]?.payload as
        | Record<string, unknown>
        | undefined;
      if (!payload || typeof payload !== "object") return [];
      const flags: string[] = [];

      // 6: la captura null/null es EL fallo que este paso se movió para
      // evitar — significa que `subscriptions/mmi-colombia` no existe en la BD del
      // run (import del paso 5 no hecho, o BD equivocada), y el paso 26
      // "restauraría" un estado que nadie leyó nunca.
      if (
        "cmmiWhitelistActiveBefore" in payload &&
        payload.cmmiWhitelistActiveBefore === null &&
        (payload as { cmmiWhitelistBefore?: unknown }).cmmiWhitelistBefore ===
          null
      ) {
        flags.push(
          "La captura salió null/null: `subscriptions/mmi-colombia` NO existe en la " +
            "BD de este run. O el import del paso 5 no llegó, o la BD es la " +
            "equivocada. El paso 27 no tendría nada real que restaurar."
        );
      }

      // 11: el paso promete "15 cambios en 4 IDs" en prod. CERO cambios es
      // legítimo sólo si ya se corrió (es idempotente); sobre una BD sin canales
      // ni horario es la base vacía disfrazada de éxito.
      const changes = payload.changes;
      if (Array.isArray(changes) && changes.length === 0) {
        const scanned =
          Number(payload.channelsScanned ?? 0) +
          Number(payload.scheduleDocsScanned ?? 0);
        flags.push(
          scanned === 0
            ? "0 cambios y 0 documentos escaneados: la BD destino no tiene canales " +
                "ni horario. Eso no es «ya estaba migrado», es una base vacía."
            : "0 cambios sobre " +
                scanned +
                " documento(s) escaneados: o ya se migró (es idempotente) o el mapeo " +
                "no toca nada de lo que hay."
        );
      }

      return flags;
    },

    /**
     * Paso 23 — el reporte de corte (#579). SÓLO LECTURA de DOS bases: los
     * puntos y el lado viejo de `legacy-2026`, quién quedó colocado y en qué
     * pierna de la BD del run. No resuelve nada y no completa el paso (eso lo
     * hace la persona, con su tiempo, cuando ya miró la salida).
     *
     * SIN ANCLAS NI ALIAS: era el paso 6 y las pedía porque RESOLVÍA el árbol en
     * memoria para poder decir qué nodos invertían. Ahora la colocación es un
     * hecho en disco.
     *
     * El nombre de la callable sale del BACKEND (`step.callable`), como en los
     * pasos auto: un mapa local aquí es el mismo drift que la consola existe para
     * evitar.
     */
    runPointsReport(): Promise<void> {
      return this.guardedStep(REPORT_STEP_ID, "pointsReport", async () => {
        const step = this.steps.find((s) => s.id === REPORT_STEP_ID);
        const callable = step?.callable?.replace(/^Migration-/, "");
        if (!callable) {
          throw new Error(
            `El paso ${this.labelOf(
              REPORT_STEP_ID
            )} no declara callable en el ` + `backend.`
          );
        }
        const result = await this.call<PointsReportPayload>(callable, {
          runId: this.runId,
        });
        this.aplicarReporte(result);
        // El JSON entero, además de la tabla: la tabla pinta 50 filas y el reporte
        // ES el insumo del paso 24 — hay que poder copiarlo completo.
        this.recordCall(
          REPORT_STEP_ID,
          true,
          "reporte de puntos (sólo lectura)",
          result
        );
      });
    },

    /**
     * Vuelca la respuesta del reporte en la pantalla. La usan las TRES llamadas
     * que devuelven un reporte —correrlo, confirmarlo y cancelar— para que no
     * haya tres formas distintas de pintar lo mismo.
     */
    aplicarReporte(result: PointsReportPayload): void {
      this.pointsReport = result.report;
      // EL ESTADO DE LOS BOTONES VIENE CON EL REPORTE, no de lo que esta
      // pantalla recuerde. Se REEMPLAZA entero en vez de fusionarse: si una
      // cuenta ya no trae bitácora es que no la tiene, y arrastrar la entrada
      // vieja dejaría un «Aplicar» apagado sin nada detrás que lo justifique.
      this.importState = Object.fromEntries(
        // `as const` para que la tupla sea `[string, ImportRowSummary]` y no
        // `(string | ImportRowSummary)[]`: sin él, `fromEntries` devuelve un
        // mapa de la unión y el tipo del estado se pierde.
        (result.imports ?? []).map((i) => [i.usernamePK, i] as const)
      );
      this.pointsConfirmation = result.confirmation;
      // El reconocimiento se APAGA en cada lectura. Es una afirmación sobre lo
      // que se acaba de ver, y lo que se ve acaba de cambiar.
      this.ackCuadreViejo = false;
    },

    /**
     * «REFRESCAR Y CONFIRMAR DATOS» — el botón que convierte la pantalla en una
     * prueba (Hector, 2026-07-26).
     *
     * El backend RELEE las dos bases y guarda ESE resultado: no se le manda
     * nada de aquí, así que es imposible firmar una foto que no salga de la base
     * en este instante. Si alguna pierna descuadra, se niega y lo dice.
     *
     * A partir de aquí la pantalla queda BLOQUEADA —los botones que escriben
     * desaparecen— y se puede marcar el paso como hecho. La salida es «Cancelar».
     */
    confirmPointsReport(): Promise<void> {
      return this.guardedStep(REPORT_STEP_ID, "confirmPoints", async () => {
        const result = await this.call<PointsReportPayload>(
          "ConfirmPointsReport",
          {
            runId: this.runId,
            // LO QUE SE RECONOCE VIAJA EXPLÍCITO. Son los descuadres del árbol
            // VIEJO, que está congelado y no se puede arreglar: el backend exige
            // que la lista coincida EXACTAMENTE con la que él detecta, así que
            // uno nuevo aparecido entre medias vuelve a negar la confirmación.
            ackViejo: this.ackCuadreViejo ? this.cuadreKeysViejo() : [],
          }
        );
        this.aplicarReporte(result);
        this.recordCall(
          REPORT_STEP_ID,
          false,
          "confirmar el reporte (guarda la foto)",
          result.confirmation
        );
        await this.load();
      });
    },

    /**
     * «CANCELAR» — borra la foto y devuelve los botones.
     *
     * Deliberadamente explícito: nada lo hace solo. El backend se niega si el
     * paso ya está marcado HECHO, porque eso dejaría un paso hecho sin prueba.
     */
    cancelPointsReport(): Promise<void> {
      return this.guardedStep(REPORT_STEP_ID, "cancelPoints", async () => {
        await this.call<unknown>("CancelPointsReport", { runId: this.runId });
        await this.load();
        // Se vuelve a leer el reporte: la pantalla que queda tiene que ser la de
        // «trabajando», con los botones puestos y la foto de ahora.
        await this.runPointsReport();
      });
    },

    /** Los descuadres detectados, o lista vacía. */
    cuadreMismatches(): CuadreLeg[] {
      return this.pointsReport?.cuadre?.mismatches ?? [];
    },
    /** Los del árbol NUEVO — los que niegan la confirmación sin escapatoria. */
    cuadreNuevos(): CuadreLeg[] {
      return this.cuadreMismatches().filter((m) => m.tree === "nuevo");
    },
    /** Los del árbol VIEJO — congelados: se reconocen, no se arreglan. */
    cuadreViejos(): CuadreLeg[] {
      return this.cuadreMismatches().filter((m) => m.tree === "viejo");
    },
    /** Las claves que hay que reconocer, tal y como las espera el backend. */
    cuadreKeysViejo(): string[] {
      return this.pointsReport?.cuadre?.keys?.viejo ?? [];
    },
    /** Los descuadres de UNA cuenta — lo que abre el icono de su celda. */
    cuadreDe(pk: string): CuadreLeg[] {
      return this.cuadreMismatches().filter((m) => m.usernamePK === pk);
    },
    /**
     * ¿DESCUADRA ESTA PIERNA DE ESTE ÁRBOL? Es lo que decide si la celda lleva
     * icono, y es la razón de que la marca viva EN LA CELDA y no en la columna
     * de acciones: la POSICIÓN dice cuál de las cuatro piernas falla sin que
     * haya que leer nada. Cada cuenta ocupa dos renglones (Izq/Der) y hay una
     * columna de puntos por árbol, así que celda = (árbol, pierna).
     */
    cuadreLeg(
      pk: string,
      tree: "viejo" | "nuevo",
      leg: "left" | "right"
    ): CuadreLeg | undefined {
      return this.cuadreMismatches().find(
        (m) => m.usernamePK === pk && m.tree === tree && m.leg === leg
      );
    },
    /**
     * El texto del icono. Lleva LAS DOS CIFRAS, no un «descuadra» a secas: el
     * `title` es lo único que se lee sin abrir nada, y con las dos cifras dentro
     * muchas veces ya no hace falta abrir.
     */
    cuadreCeldaTitulo(
      pk: string,
      tree: "viejo" | "nuevo",
      leg: "left" | "right"
    ): string {
      const m = this.cuadreLeg(pk, tree, leg);
      if (!m) return "";
      return (
        `Descuadra: el resumen dice ${this.puntos(m.resumen.pending)} y sus ` +
        `documentos suman ${this.puntos(m.consolidado.pending)} ` +
        `(${this.cuadreDiferencia(m)}). Pulsa para ver el detalle de ${pk}.`
      );
    },
    /** Abre el pop-up. Sin argumento, con todos. */
    verCuadre(pk?: string): void {
      this.cuadreVistas = pk ? this.cuadreDe(pk) : this.cuadreMismatches();
      this.cuadreTitulo = pk ? pk : "todas las cuentas";
    },
    cerrarCuadre(): void {
      this.cuadreVistas = null;
    },
    /** «DERRAME (left)» — la etiqueta primero, que es como se lee la tabla. */
    cuadrePierna(m: CuadreLeg): string {
      const etiqueta =
        m.label === "unknown" ? "sin etiqueta" : m.label.toUpperCase();
      return `${etiqueta} (${m.leg})`;
    },
    /** El rótulo del árbol, con su base entre paréntesis. */
    cuadreArbol(m: CuadreLeg): string {
      const nombre = m.tree === "viejo" ? "árbol VIEJO" : "árbol NUEVO";
      return m.dbId ? `${nombre} · ${m.dbId}` : nombre;
    },
    /**
     * La diferencia YA INTERPRETADA. Dice la dirección, que es lo que decide la
     * acción: no es lo mismo declarar puntos que no existen que tener registros
     * que nadie cuenta.
     */
    cuadreDiferencia(m: CuadreLeg): string {
      return m.diff > 0
        ? `+${this.puntos(m.diff)} en el resumen`
        : `+${this.puntos(-m.diff)} en el detalle`;
    },
    // ═══════════════════════════════════════════════════════════════════════
    // LOS FORMULARIOS DE OTRA CONSOLA — una pestaña por instancia
    // ═══════════════════════════════════════════════════════════════════════
    //
    // EL PROBLEMA QUE RESUELVEN. El paso de las extensiones pide crear DIEZ
    // instancias de 25 campos cada una, a mano, en la consola de Firebase. Era
    // un muro de texto: el operador iba del formulario al runbook y de vuelta,
    // diez veces, con la red congelada y sin nada que le dijera por cuál iba.
    //
    // Una pestaña por instancia con sus 25 campos EN EL ORDEN DEL FORMULARIO
    // convierte eso en copiar de arriba abajo sin saltar, y el tic de «ya
    // configurada» es lo que contesta «¿por dónde iba?» después de un refresco.

    // ═══════════════════════════════════════════════════════════════════════
    // LOS GATES: rojo es rojo
    // ═══════════════════════════════════════════════════════════════════════

    /** Los dos pasos que emiten un VEREDICTO: el gate del 18 y el de descongelar. */
    isGateStep(stepId: string): boolean {
      return GATE_STEPS.includes(stepId);
    },
    /** El informe de ESE gate, si lo hay. Que falte es un estado legítimo. */
    gateReportOf(stepId: string): GateReport | undefined {
      return this.gateReports[stepId];
    },
    /** Las invariantes que comprueba ESE gate. Vacío en cualquier otro paso. */
    catalogOf(stepId: string): InvariantCatalogEntry[] {
      return GATE_CATALOGS[stepId] ?? [];
    },
    /** Qué habilita el verde de ESE gate. Una línea: son dos puertas distintas. */
    gateOkNote(stepId: string): string {
      return stepId === "gate-unfreeze--c"
        ? "Se puede descongelar: es la última compuerta antes de abrir la red."
        : "Se puede continuar a la validación humana de los árboles y, después, al descongelamiento.";
    },
    /**
     * ¿ESTÁ ESTE GATE CORRIENDO AHORA MISMO?
     *
     * Sus TRES botones cuentan: el dry-run, el «Ejecutar» y el «Continuar a
     * pesar del rojo» —que vuelve a correr el gate entero, no es un atajo—. Con
     * mirar sólo el segundo, aceptar un rojo dejaba en pantalla el informe viejo
     * durante todo el minuto que tarda la relectura, que es el mismo fallo por
     * el que existe este método.
     */
    gateRunning(stepId: string): boolean {
      return (
        this.isRunning(this.autoAction(stepId, false)) ||
        this.isRunning(this.autoAction(stepId, true)) ||
        this.isRunning(this.acceptRedAction(stepId))
      );
    },
    /**
     * (Y NO MIENTRAS SE RE-LEE: el estado del paso es el de la corrida anterior
     * hasta que la callable vuelve, así que durante ese minuto este aviso —y su
     * botón de salida— hablarían de un rojo que ya puede no existir. Ver
     * `gateRunning`.)
     *
     * ¿Está este gate en rojo Y sin resolver? Se mira el ESTADO, que desde el
     * arreglo del veredicto ya es `failed` cuando el informe trae `ok: false`.
     * Mirar el estado y no el informe es lo correcto: es el estado el que
     * bloquea a los demás pasos, así que es el que tiene que mandar en la
     * pantalla o volverían a decir cosas distintas.
     */
    gateIsRed(step: MigrationStepDef): boolean {
      if (this.gateRunning(step.id)) return false;
      return this.isGateStep(step.id) && this.statusOf(step.id) === "failed";
    },
    /**
     * ¿Está marcado hecho CON el informe en rojo? Es la huella de la salida
     * deliberada, y tiene que verse: un paso hecho cuyo veredicto sigue rojo no
     * puede parecerse a uno que pasó limpio.
     */
    gateRedAccepted(step: MigrationStepDef): boolean {
      if (!this.isGateStep(step.id)) return false;
      // Mientras se re-lee, lo guardado es de la corrida anterior. Ver `gateIsRed`.
      if (this.gateRunning(step.id)) return false;
      if (this.statusOf(step.id) !== "done") return false;
      const informe = this.stateOf(step.id)?.result as
        | { ok?: boolean }
        | undefined;
      return informe?.ok === false;
    },
    /**
     * LA SALIDA, pulsada a conciencia. Vuelve a correr el gate con `acceptRed`:
     * lo relee —así lo que se acepta es el rojo de AHORA, no el de hace media
     * hora— y el backend marca el paso hecho dejando escrito quién fue y qué
     * falla. No abre la red: descongelar vuelve a leer el veredicto.
     */
    acceptRedGate(step: MigrationStepDef): Promise<void> {
      return this.runAuto({
        stepId: step.id,
        dryRun: false,
        data: { acceptRed: true },
        action: this.acceptRedAction(step.id),
      });
    },
    /** La clave del spinner del botón rojo, en UN SOLO SITIO (ver `runAuto`). */
    acceptRedAction(stepId: string): string {
      return `acceptRed:${stepId}`;
    },

    /** Las instancias que declara el paso. Vacío en casi todos. */
    stepInstances(step: MigrationStepDef): MigrationStepInstance[] {
      return step.instances ?? [];
    },
    /** Las marcadas como configuradas en este paso. */
    instancesDoneOf(stepId: string): string[] {
      return this.instancesDone[stepId] ?? [];
    },
    instanceIsDone(stepId: string, instanceId: string): boolean {
      return this.instancesDoneOf(stepId).includes(instanceId);
    },
    /**
     * Marca o DESMARCA. Las dos direcciones, porque una instancia se puede
     * reconfigurar: un tic que no se pudiera quitar obligaría a recordar «ésta
     * está marcada pero la volví a tocar», que es justo lo que esto evita.
     */
    toggleInstanceDone(stepId: string, instanceId: string): void {
      const actuales = this.instancesDoneOf(stepId);
      const siguiente = actuales.includes(instanceId)
        ? actuales.filter((id) => id !== instanceId)
        : [...actuales, instanceId];
      this.instancesDone = { ...this.instancesDone, [stepId]: siguiente };
      writeInstancesDone(this.runId, stepId, siguiente);
    },
    /** «4 de 10» — el progreso, sin abrir ninguna pestaña. */
    instancesDoneCount(step: MigrationStepDef): number {
      const ids = new Set(this.instancesDoneOf(step.id));
      // Se cuenta contra las instancias QUE HAY, no contra la lista guardada: si
      // el paso cambia y una instancia desaparece, su marca vieja no puede
      // inflar el contador y dar por terminado lo que no lo está.
      return this.stepInstances(step).filter((i) => ids.has(i.id)).length;
    },
    /** Las que faltan, por nombre. Es lo que dice el aviso de «no se puede dar por hecho». */
    instancesPending(step: MigrationStepDef): string[] {
      const ids = new Set(this.instancesDoneOf(step.id));
      return this.stepInstances(step)
        .filter((i) => !ids.has(i.id))
        .map((i) => i.id);
    },
    /** La pestaña abierta; por defecto, la primera. */
    activeInstance(step: MigrationStepDef): string {
      const elegida = this.instanceTab[step.id];
      const instancias = this.stepInstances(step);
      if (elegida && instancias.some((i) => i.id === elegida)) return elegida;
      return instancias.length ? instancias[0].id : "";
    },
    selectInstance(stepId: string, instanceId: string): void {
      this.instanceTab = { ...this.instanceTab, [stepId]: instanceId };
    },
    /** La instancia que se está mirando, entera. */
    shownInstance(step: MigrationStepDef): MigrationStepInstance | undefined {
      const id = this.activeInstance(step);
      return this.stepInstances(step).find((i) => i.id === id);
    },
    /**
     * ¿Esta acción significa NO HAGAS NADA?
     *
     * Es la única de las cuatro que no manda trabajo, y por eso se pinta
     * distinto: `NUEVA`, `ACTUALIZAR` y `RENOMBRAR desde …` son tareas; ésta es
     * lo contrario de una tarea. Se compara por PREFIJO, igual que `RENOMBRAR`
     * lleva su origen detrás — el día que ésta lleve un motivo pegado («NO SE
     * TOCA — se queda en 0.1.55») sigue reconociéndose.
     */
    actionIsHandsOff(action?: string): boolean {
      return (action ?? "").trim().toUpperCase().startsWith("NO SE TOCA");
    },
    /**
     * El texto del botón de marcar. «Configurada» es mentira en las dos que no
     * se tocan: ahí no se configura nada, se lee la nota y se sigue — y un botón
     * que dice «marcar como configurada» en una instancia que no hay que tocar
     * es una invitación a ir a Firebase a tocarla.
     */
    instanceDoneLabel(step: MigrationStepDef): string {
      const inst = this.shownInstance(step);
      const quieta = this.actionIsHandsOff(inst?.action);
      if (inst && this.instanceIsDone(step.id, inst.id)) {
        return quieta
          ? "Ya leída — pulsa para desmarcar"
          : "Ya configurada — pulsa para desmarcar";
      }
      return quieta ? "Marcar como ya leída" : "Marcar como ya configurada";
    },
    /** El porqué del botón, en el tooltip. Mismo criterio que la etiqueta. */
    instanceDoneTitle(step: MigrationStepDef): string {
      const inst = this.shownInstance(step);
      const quieta = this.actionIsHandsOff(inst?.action);
      if (inst && this.instanceIsDone(step.id, inst.id)) {
        return "Pulsa para DESmarcarla: una instancia se puede volver a mirar.";
      }
      return quieta
        ? "Esta instancia no se toca. Márcala cuando hayas leído su nota."
        : "Márcala cuando el formulario esté guardado en Firebase.";
    },
    /**
     * ¿Este campo es de los que NO se pueden cambiar después de instalar? Ver
     * `IRREVERSIBLE_PARAMS`: es la única advertencia del bloque que no se deduce
     * mirando el formulario de Firebase.
     */
    paramIsIrreversible(label: string): boolean {
      return IRREVERSIBLE_PARAMS.has(label);
    },
    /**
     * ¿Por qué NO se puede marcar «Hecho»? Cadena vacía = se puede.
     *
     * Hoy sólo lo contesta el paso del reporte de puntos, que es el único con
     * foto. El día que el del cofundador o el del catálogo tengan la suya, esto
     * es donde entran — la lista de pasos con foto la manda el backend
     * (`SNAPSHOT_STEPS`), y este método sólo pinta lo que ya sabe la pantalla.
     */
    doneBlockedReason(step: MigrationStepDef): string {
      // LOS FORMULARIOS SIN MARCAR. Dar el paso por hecho con instancias sin
      // configurar deja la mitad de la exportación a BigQuery sin crear, y eso
      // no truena: la tabla simplemente no aparece, semanas después.
      const faltan = this.instancesPending(step);
      if (this.stepInstances(step).length && faltan.length) {
        return (
          `Faltan ${faltan.length} instancia(s) por marcar como atendidas: ` +
          `${faltan.join(", ")}. Márcalas en su pestaña según las vayas ` +
          `terminando — si alguna no hace falta, márcala igual, pero a sabiendas.`
        );
      }
      if (step.id !== REPORT_STEP_ID) return "";
      if (this.reportConfirmed()) return "";
      return (
        "Falta confirmar los datos: pulsa «Refrescar y confirmar datos» en la " +
        "tabla del reporte. Este reporte deja de poder reconstruirse en cuanto " +
        "se descongele la red, así que el paso no se cierra sin su foto."
      );
    },
    /** ¿Está la foto firmada? El interruptor de toda la pantalla. */
    reportConfirmed(): boolean {
      return this.pointsConfirmation?.confirmed === true;
    },
    /** La hora de la firma, para el letrero. */
    reportConfirmedAt(): string {
      const at = this.pointsConfirmation?.confirmedAt;
      return at ? new Date(at).toLocaleString() : "";
    },
    /**
     * ¿Se puede pulsar «Confirmar»? Con descuadres del árbol nuevo NO, porque el
     * backend va a negarse: el botón apagado con su motivo delante es mejor que
     * un error después de pulsar.
     */
    confirmBlockedReason(): string {
      if (!this.pointsReport) return "Corre el reporte primero.";
      if (this.cuadreNuevos().length) {
        return (
          `Hay ${this.cuadreNuevos().length} pierna(s) del árbol NUEVO cuyo ` +
          `resumen no cuadra con sus documentos. Se arregla desde esta misma ` +
          `tabla (Borrar / volver a importar o crear) antes de firmar.`
        );
      }
      if (this.cuadreViejos().length && !this.ackCuadreViejo) {
        return (
          `Marca la casilla de reconocimiento: hay ${
            this.cuadreViejos().length
          } pierna(s) del árbol VIEJO que no cuadran, y esa base está congelada ` +
          `— no se arreglan, se deciden a mano.`
        );
      }
      return "";
    },

    /**
     * Lee los DIEZ documentos del catálogo y los pinta. No escribe, no resuelve
     * y NO COMPLETA EL PASO: el paso lo cierra la persona cuando ha mirado los
     * números, que es justo lo que ningún programa puede hacer por ella.
     *
     * El botón tampoco se apaga al marcar el paso hecho (`unlocked`, no
     * `isDone`): se re-corre después de cada ajuste, que es como se usa —
     * mirar, tocar dos booleanos a mano, volver a mirar.
     *
     * El nombre de la callable es local (`CATALOG_CALLABLE`) y no sale de
     * `step.callable`. El porqué, entero, está en la constante.
     */
    runVerifyCatalog(): Promise<void> {
      return this.guardedStep(CATALOG_STEP_ID, "verifyCatalog", async () => {
        const result = await this.call<{ report: CatalogReport }>(
          CATALOG_CALLABLE,
          { runId: this.runId }
        );
        this.catalogReport = result.report;
        // El JSON entero además de las tablas: la pantalla resume, y lo que se
        // pega en el documento del runbook tiene que llevar los diez completos.
        this.recordCall(
          CATALOG_STEP_ID,
          true,
          "lectura del catálogo (sólo lectura)",
          result
        );
      });
    },

    /**
     * MARCAR o DESMARCAR una cuenta. La selección vive AQUÍ, no en la tabla:
     * es lo que viaja a la callable, así que su dueño es quien la llama.
     *
     * Se reemplaza el array entero en vez de mutarlo (`push`/`splice`) porque
     * varias cosas de esta pantalla dependen de él —el `disabled` del botón, su
     * `title`, el contador— y una mutación en sitio no siempre las despierta.
     */
    toggleCofounder(usernamePK: string): void {
      this.cofounderSelection = this.cofounderSelection.includes(usernamePK)
        ? this.cofounderSelection.filter((u) => u !== usernamePK)
        : [...this.cofounderSelection, usernamePK];
    },

    /**
     * REFRESCAR LA TABLA. Sólo lee, y `action: "read"` hace que la callable NO
     * pase por `runStep`: no marca el paso, no deja rastro en el run y se puede
     * pulsar las veces que haga falta, antes y después de aplicar.
     *
     * `guardedStep` y no `guarded`: un fallo tiene que aterrizar en el error de
     * ESTA tarjeta, no en un mensaje suelto arriba del todo.
     */
    readCofounderRanks(): Promise<void> {
      return this.guardedStep(COFOUNDER_STEP_ID, "cofounder:read", async () => {
        const result = await this.call<CofounderResult>(COFOUNDER_CALLABLE, {
          runId: this.runId,
          action: "read",
        });
        this.cofounderReport = result.report;
        // Un «escrito ahora mismo» que sobreviviera a la siguiente lectura se
        // leería como si acabara de pasar.
        this.cofounderWrites = [];
        this.recordCall(
          COFOUNDER_STEP_ID,
          true,
          "lectura de los fundadores (sólo lectura)",
          result
        );
      });
    },

    /**
     * EJECUTAR EL PASO sobre lo marcado.
     *
     * Va por `runAuto`, el MISMO runner que los demás pasos automáticos: de ahí
     * salen el nombre de la callable (leído de `step.callable`, no de una tabla
     * local), el marcado del paso, el registro en el run y la recarga. Lo único
     * propio es la carga útil — `usernames`, la selección — y viaja por el canal
     * que ya existía (`data`), el mismo por el que el paso de los rangos manda
     * su JSON. No hay vía nueva.
     *
     * Después se REFRESCA la tabla y se VACÍA la selección. Las dos cosas por el
     * mismo motivo: tras escribir, las filas ya no dicen lo que decían, y unas
     * casillas que sobreviven a la escritura invitan a un segundo clic que ya no
     * significa lo mismo.
     */
    async applyCofounderRanks(): Promise<void> {
      const marcadas = [...this.cofounderSelection];
      if (!marcadas.length) return;
      await this.runAuto({
        stepId: COFOUNDER_STEP_ID,
        dryRun: false,
        data: { usernames: marcadas },
      });
      // Si el paso falló, `guardedStep` ya dejó el error en la tarjeta y la
      // selección se conserva: quien reintenta no tiene que volver a marcarlo
      // todo. Sólo se limpia cuando de verdad se escribió.
      if (this.stepError[COFOUNDER_STEP_ID]) return;
      this.cofounderSelection = [];
      const result = this.lastCall[COFOUNDER_STEP_ID]?.payload as
        | CofounderResult
        | undefined;
      if (result?.report) {
        this.cofounderReport = result.report;
        this.cofounderWrites = result.writes ?? [];
      }
    },

    /**
     * ¿Por qué esta fila NO lleva botones? Devuelve el motivo, o "" si sí los
     * lleva. Lo ÚNICO bloqueado es el ápice: no es un miembro colocado, no hay
     * pierna destino donde escribir, y el backend lo refusaría igual.
     *
     * Se enseña el motivo en vez de esconder la fila: una cuenta que desaparece
     * de la tabla se lee como «no aplica», y ésta sí aplica — lo que pasa es que
     * su saldo lo resuelve otra cosa.
     */
    rowBlockedReason(row: PointsReportRow): string {
      // CORTO: se pinta DENTRO de la celda, y ahí es un aviso, no documentación.
      if (row.resolvedBinarySide === null)
        return "ápice: no hay pierna donde aplicar";
      return "";
    },
    /**
     * ¿Esta fila lleva ADVERTENCIA? Devuelve el aviso entero, o "" si no lleva.
     *
     * Las cuentas de pierna infinita no están prohibidas: están AVISADAS. La
     * migración les crea piernas nuevas, así que copiar encima lo más probable es
     * que duplique lo que el propio proceso acaba de generar — pero «lo más
     * probable» no es «siempre», y estas cuentas se resuelven una por una.
     *
     * El texto dice el POR QUÉ en lenguaje de operador, no el nombre de la
     * bandera: quien mira esta tabla dentro de la ventana no tiene por qué saber
     * qué es `expirationExempt`.
     */
    rowWarningReason(row: PointsReportRow): string {
      // Una frase. Es un tooltip de una celda de tabla, no una nota del runbook:
      // el porqué largo vive en la cabecera de `binaryPointsImport.ts`.
      if (row.expirationExempt)
        return (
          "Pierna infinita: la migración le crea piernas nuevas, así que " +
          "aplicar aquí puede duplicar. Reversible con «Borrar»."
        );
      return "";
    },
    /**
     * 🔴 EL SALDO QUE NADIE RESPALDA, por ETIQUETA y ya en el marco del árbol
     * nuevo. Es lo que hace que la fila salga marcada «a revisar» y sin
     * «Aplicar»: el import copia DOCUMENTOS, y aquí el nodo declara por encima
     * de los que tiene.
     *
     * ⚠️ NO ES LO QUE SE PROPONE CREAR, y lo fue hasta el 2026-07-26. Ver
     * `montoPropuesto`: la caja nace con el CONSOLIDADO, no con esta diferencia.
     * Esto de aquí ya sólo AVISA.
     *
     * De dónde sale: `nodeMinusDetail` es «lo que el nodo viejo declara menos lo
     * que suman sus registros vivos», pierna a pierna del árbol VIEJO. Sólo
     * cuenta el signo POSITIVO (nodo por encima del detalle), que es la
     * dirección que pierde dinero; la contraria la corrige el propio import.
     *
     * Y se traduce a etiqueta, no a pierna física, porque es el marco en el que
     * se decide: la pierna de DERRAME del árbol viejo es su `legacyBinarySide`,
     * así que lo que sobra en el derrame es `nodeMinusDetail[legacyBinarySide]`.
     */
    sinRespaldo(row: PointsReportRow): {
      interna: number;
      derrame: number;
      total: number;
    } {
      const d = row.nodeMinusDetail;
      const vacio = { interna: 0, derrame: 0, total: 0 };
      if (!d || !row.legacyBinarySide) return vacio;
      const otro = row.legacyBinarySide === "left" ? "right" : "left";
      const derrame = Math.max(0, d[row.legacyBinarySide]);
      const interna = Math.max(0, d[otro]);
      return { interna, derrame, total: interna + derrame };
    },
    /** ¿Esta fila hay que resolverla A MANO? Ver `sinRespaldo`. */
    esARevisar(row: PointsReportRow): boolean {
      return this.sinRespaldo(row).total > 0;
    },
    /**
     * ¿Es una de las dos cuentas que reciben la PIERNA INFINITA? Ver
     * `PIERNAS_INFINITAS` — son dos, no las seis exentas de caducidad.
     */
    esPiernaInfinita(row: PointsReportRow): boolean {
      return PIERNAS_INFINITAS.includes(row.usernamePK);
    },
    /**
     * EL «SUMMARY» DE UNA PIERNA: lo que DECLARA el nodo del árbol viejo, por
     * etiqueta. Es la misma cifra de las columnas «derrame/interna · árbol
     * viejo», repetida al lado de la caja para no obligar a recorrer la tabla
     * con el dedo ni a abrir el pop-up del descuadre.
     *
     * No lo sostiene ningún documento: es un acumulado que se mantiene aparte
     * del detalle, y de esa familia son los 98 summaries que salieron con `NaN`.
     */
    summaryDe(row: PointsReportRow, label: "interna" | "derrame"): number {
      return label === "derrame"
        ? this.derramePoints(row)
        : this.internaPoints(row);
    },
    /**
     * 🔴 EL «CONSOLIDADO» DE UNA PIERNA: lo que suman sus REGISTROS VIVOS, ya
     * puestos en la pierna del árbol NUEVO. Es exactamente lo que escribiría
     * «Aplicar», y por eso es la cifra con la que nace la caja de crear.
     *
     * `willBe` viene YA CRUZADO del backend —se calcula en destino—, así que se
     * indexa con `derrameSide`/`internaSide`, nunca con `legacyBinarySide`.
     *
     * EL RESPALDO es por si contesta un backend anterior a ese campo: el mismo
     * número sale de `summary − nodeMinusDetail`, pero OJO —`nodeMinusDetail`
     * está en el marco VIEJO—, y por eso ahí sí se indexa con la pierna legacy.
     */
    consolidadoDe(row: PointsReportRow, label: "interna" | "derrame"): number {
      const lado = label === "derrame" ? row.derrameSide : row.internaSide;
      if (row.willBe && lado) return Math.max(0, row.willBe[lado]);
      const d = row.nodeMinusDetail;
      if (!d || !row.legacyBinarySide) return 0;
      const otro = row.legacyBinarySide === "left" ? "right" : "left";
      const legacyLeg = label === "derrame" ? row.legacyBinarySide : otro;
      return Math.max(0, this.summaryDe(row, label) - d[legacyLeg]);
    },
    /**
     * ¿Se contradicen los dos números de esa pierna? Sólo tiñe: la fila no está
     * rota, pide una decisión.
     */
    fuentesDifieren(
      row: PointsReportRow,
      label: "interna" | "derrame"
    ): boolean {
      return this.summaryDe(row, label) !== this.consolidadoDe(row, label);
    },
    /**
     * EL IMPORTE CON EL QUE NACE CADA CAJA. Se calcula una vez y a partir de ahí
     * manda lo que haya escrito en el campo: es una propuesta, no un veredicto.
     *
     *   pierna infinita   los 900.000.000 de la concesión, en el DERRAME. Ese
     *                     saldo no se ganó, se OTORGA, así que no sale de ningún
     *                     registro — y teclear nueve dígitos a mano dentro de la
     *                     ventana es la clase de error que no se ve hasta que el
     *                     binario paga mal.
     *   todo lo demás     el CONSOLIDADO de esa pierna: lo que respaldan sus
     *                     registros vivos.
     *
     * ⚠️ NO ES «LA DIFERENCIA», y lo fue hasta el 2026-07-26. Proponía
     * `summary − consolidado`, o sea justo los puntos que NINGÚN documento
     * sostiene: en `eworre` ofrecía crear 13.600 para llegar a los 14.100 que
     * declara un nodo cuyos registros vivos suman 500. Y crear puntos es de IDA
     * —`remove` está deshabilitado y la caducidad nace de hoy—, así que esa cifra
     * de más no se deshace con otro clic.
     *
     * La decisión (Hector, 2026-07-26): «el correcto debe de ser el consolidado,
     * porque son los que están respaldados por algo. Si son más los que tiene el
     * summary, debería poderlos agregar a mano». Por eso el campo es editable y
     * los dos números están a la vista.
     */
    montoPropuesto(row: PointsReportRow, label: "interna" | "derrame"): number {
      if (this.esPiernaInfinita(row) && label === "derrame")
        return PUNTOS_INFINITOS;
      return this.consolidadoDe(row, label);
    },
    /** El importe vivo del campo, con la propuesta como valor inicial. */
    montoDe(row: PointsReportRow, label: "interna" | "derrame"): number {
      const k = `${row.usernamePK}:${label}`;
      const v = this.crearMonto[k];
      return v === undefined ? this.montoPropuesto(row, label) : v;
    },
    setMonto(
      row: PointsReportRow,
      label: "interna" | "derrame",
      value: string
    ): void {
      const n = Math.trunc(Number(value));
      this.crearMonto = {
        ...this.crearMonto,
        [`${row.usernamePK}:${label}`]: Number.isFinite(n) && n > 0 ? n : 0,
      };
    },
    /**
     * CREAR PUNTOS A MANO en una pierna, por ETIQUETA.
     *
     * La etiqueta viaja; la pierna física NO. El backend la calcula leyendo el
     * `binarySide` del árbol nuevo (derrame = su lado, interna = la contraria),
     * así que si mañana cambia la colocación del ancla esto sigue apuntando
     * bien. Mandar `left`/`right` desde aquí sería fijar hoy una respuesta que
     * depende de un dato que aún puede moverse.
     *
     * PREGUNTA SIEMPRE, y enseña la cifra con separadores: novecientos millones
     * sin puntos no se leen, y este botón escribe dinero de verdad. Lo que se
     * repinta después es la fila RELEÍDA de Firestore, no lo que creíamos.
     *
     * ⚠️ EL DIÁLOGO DICE QUE **SUMA**, y tiene que decirlo antes de confirmar:
     * crear puntos es de IDA —`remove` está deshabilitado en la herramienta— así
     * que pulsar dos veces deja el doble, y la única marcha atrás es «Borrar»,
     * que deshace TODO lo que esta fila lleva escrito, no la última pulsación.
     */
    crearPuntos(
      row: PointsReportRow,
      label: "interna" | "derrame"
    ): Promise<void> {
      const amount = this.montoDe(row, label);
      if (!(amount > 0)) return Promise.resolve();
      const enLaPierna =
        label === "derrame" ? this.derrameNow(row) : this.internaNow(row);
      const yaTiene = enLaPierna ?? 0;
      const sigue = window.confirm(
        `${row.usernamePK}\n\n` +
          `Se van a CREAR ${amount.toLocaleString()} puntos en su pierna de ` +
          `${label.toUpperCase()} del árbol nuevo.\n\n` +
          (yaTiene > 0
            ? `⚠️ Esa pierna YA TIENE ${yaTiene.toLocaleString()} puntos, y ` +
              `esto SE SUMA: quedaría en ${(
                yaTiene + amount
              ).toLocaleString()}.\n\n`
            : "") +
          `No se copian de ningún sitio: se crean, con la fecha de hoy, y ` +
          `quedan en la bitácora a tu nombre.\n\n` +
          `Quitar puntos no está habilitado: la única marcha atrás es ` +
          `«Borrar», que deshace TODO lo escrito en esta fila.\n\n¿Seguir?`
      );
      if (!sigue) return Promise.resolve();
      return this.guardedStep(
        REPORT_STEP_ID,
        `crear:${label}:${row.usernamePK}`,
        async () => {
          const result = await this.call<{
            state: ImportRowState;
            result?: { applied?: boolean; note?: string };
          }>("CreateBinaryPoints", {
            runId: this.runId,
            usernamePK: row.usernamePK,
            label,
            amount,
            // `atMs`+`rand` se acuñan AQUÍ, uno por confirmación. Son lo que
            // distingue «reintento del mismo clic» (se deduplica) de «crear otra
            // vez» (se escribe), y de ellos sale la clave de idempotencia. El
            // backend los EXIGE justo para que nadie los rellene por su cuenta.
            atMs: Date.now(),
            rand: String(Math.floor(Math.random() * 1000)).padStart(3, "0"),
          });
          // Silencio NO. Si el backend contesta que no escribió nada, se dice —
          // confirmar y que no pase nada es lo que hace perder el rato.
          if (result.result && result.result.applied === false) {
            throw new Error(
              `No se escribió nada: ${
                result.result.note ?? "el ajuste ya estaba registrado"
              }.`
            );
          }
          if (this.pointsReport && result.state?.row) {
            this.pointsReport = {
              ...this.pointsReport,
              rows: this.pointsReport.rows.map((r) =>
                r.usernamePK === row.usernamePK ? result.state.row : r
              ),
            };
          }
          this.recordCall(
            REPORT_STEP_ID,
            false,
            `crear ${label} · ${row.usernamePK} · ${amount}`,
            result
          );
        }
      );
    },
    /** ¿Hay un import APLICADO para esta cuenta en este run? */
    importApplied(usernamePK: string): boolean {
      return this.importState[usernamePK]?.status === "applied";
    },
    /**
     * ¿Hay un import A MEDIO APLICAR? La bitácora se queda en `applying` cuando
     * la copia se cortó entre lotes: las cuentas grandes no caben en un solo
     * commit de Firestore y se escriben troceadas, así que un fallo a mitad deja
     * documentos puestos y la bitácora sin cerrar.
     *
     * NO es «aplicado» —la copia no está entera— ni «sin hacer» —hay cosas
     * escritas—. Es su propio estado, y el único botón que vale es «Borrar».
     */
    importApplying(usernamePK: string): boolean {
      return this.importState[usernamePK]?.status === "applying";
    },
    /**
     * ¿Hay ALGO escrito que borrar? Es lo que enciende «Borrar», y por eso no
     * pregunta por «aplicado» sino por «aplicado O a medias».
     *
     * Dejarlo apagado sobre un import cortado encerraría a la cuenta: «Aplicar»
     * la refusa —reaplicar encima estropearía el estado que guarda para
     * deshacer— y «Borrar» no se podría pulsar, con lo que la única salida
     * quedaría fuera de la consola.
     */
    importBorrable(usernamePK: string): boolean {
      return this.importApplied(usernamePK) || this.importApplying(usernamePK);
    },
    /** ¿El árbol cambió debajo desde que se aplicó? */
    importStale(usernamePK: string): boolean {
      return this.importState[usernamePK]?.staleFingerprint === true;
    },
    /** El resumen de lo escrito, para el `title` de la insignia. */
    importSummary(usernamePK: string): string {
      const st = this.importState[usernamePK];
      if (!st) return "";
      return (
        `${st.totals.records} registro(s) · ${st.totals.pending} pts` +
        (st.crossed ? " · piernas CRUZADAS" : "")
      );
    },
    /**
     * ¿POR QUÉ ESTÁ APAGADO «Aplicar»? Devuelve el motivo, o "" si se puede.
     *
     * Un botón deshabilitado que no dice por qué se lee como una avería: quien
     * mira la fila a las 3am no sabe si le falta un permiso, si el paso está
     * bloqueado o si es que ya lo hizo. El motivo va al `title`.
     *
     * El orden importa: la huella obsoleta se comprueba ANTES que el «ya
     * aplicado», porque las dos son ciertas a la vez y la que hay que contar es
     * la que dice qué hacer a continuación (borrar y rehacer, no «ya está»).
     */
    applyDisabledReason(usernamePK: string): string {
      // Una línea cada uno: dicen QUÉ pasa y QUÉ hacer, y nada más.
      if (this.importStale(usernamePK))
        return "El árbol cambió debajo: borra y vuelve a aplicar.";
      // Antes que el «ya aplicado» por lo mismo que la huella obsoleta: las dos
      // se parecen en la pantalla y sólo una dice qué hacer ahora.
      if (this.importApplying(usernamePK))
        return "Quedó a medio aplicar: borra antes de volver a aplicar.";
      if (this.importApplied(usernamePK)) {
        const resumen = this.importSummary(usernamePK);
        return `Ya aplicado (${resumen}). Borra primero.`;
      }
      return "";
    },
    /** ¿POR QUÉ está apagado «Borrar»? Ver `applyDisabledReason`. */
    undoDisabledReason(usernamePK: string): string {
      if (this.importBorrable(usernamePK)) return "";
      if (this.importState[usernamePK]?.status === "undone")
        return "Ya se borró.";
      return "Nada que borrar: no se ha aplicado.";
    },
    /**
     * PUNTOS CON SEPARADOR DE MILES, como el nodo del árbol
     * (`OrgTreeNode.vue`). `toLocaleString()` SIN locale explícito: usa el del
     * navegador, que es el mismo criterio que ya se ve en la Vista de Árbol.
     * Truncado porque un punto es una unidad entera.
     */
    puntos(value: number): string {
      return Number.isFinite(value) ? Math.trunc(value).toLocaleString() : "0";
    },
    /** La letra de la pastilla de lado: la misma `I`/`D` del nodo del árbol. */
    sideLetter(side: "left" | "right" | null): string {
      if (side === "left") return "I";
      if (side === "right") return "D";
      return "—";
    },
    /** La clase de color de la pastilla — calca `.otn-side-l` / `.otn-side-r`. */
    sideClass(side: "left" | "right" | null): string {
      if (side === "left") return "pr-side-l";
      if (side === "right") return "pr-side-r";
      return "pr-side-none";
    },
    /**
     * El `title` de la pastilla, en palabras. `arbol` es «nuevo» o «viejo»: con
     * dos árboles en la misma tabla, un tooltip que sólo diga «Izquierda» no
     * resuelve la duda que hace que alguien pase el ratón por encima.
     */
    sideTitle(side: "left" | "right" | null, arbol: string): string {
      if (side === "left") return `Izquierda, en el árbol ${arbol}`;
      if (side === "right") return `Derecha, en el árbol ${arbol}`;
      return `Sin lado en el árbol ${arbol} (el ápice no cuelga de nadie)`;
    },
    /**
     * APLICAR o BORRAR el import de UNA cuenta.
     *
     * Lo que vuelve es LA FILA RELEÍDA DE FIRESTORE, y con eso se repinta: la
     * fila de la tabla se sustituye por la que devuelve el servidor. Nada
     * optimista — si la escritura fue parcial, la tabla lo enseña, que es
     * justamente lo que hay que ver a las tres de la mañana.
     *
     * Va por `guardedStep`, así que un fallo aterriza en el error del paso (y no
     * en un `alert` suelto) y `busy` bloquea TODOS los botones mientras hay una
     * llamada en vuelo: no se puede pulsar dos veces por impaciencia.
     */
    importRow(usernamePK: string, action: "apply" | "undo"): Promise<void> {
      // LA ÚNICA PREGUNTA DEL PASO, y sólo al APLICAR sobre una fila AVISADA.
      // «Borrar» no pregunta —deshacer no necesita permiso—, y los renglones
      // normales tampoco: un `confirm` en las ~40 cuentas corrientes se contesta
      // con el codo a la tercera fila y deja de frenar justo donde hacía falta.
      const row = this.pointsReport?.rows.find(
        (r) => r.usernamePK === usernamePK
      );
      const aviso = row ? this.rowWarningReason(row) : "";
      if (action === "apply" && aviso) {
        const sigue = window.confirm(
          `${usernamePK}\n\n${aviso}\n\n¿Aplicar de todas formas?`
        );
        if (!sigue) return Promise.resolve();
      }
      return this.guardedStep(
        REPORT_STEP_ID,
        `import:${action}:${usernamePK}`,
        async () => {
          const result = await this.call<{ state: ImportRowState }>(
            "ImportBinaryPoints",
            { runId: this.runId, usernamePK, action }
          );
          // La bitácora que acaba de leer el servidor, aplanada a lo que los dos
          // botones necesitan saber. Si no hay bitácora la entrada se QUITA: un
          // `undefined` colgando haría que `importApplied` contestara false por
          // el camino largo, y este mapa se lee en cada repintado de la tabla.
          const next = { ...this.importState };
          if (result.state.audit) {
            next[usernamePK] = {
              usernamePK,
              status: result.state.audit.status,
              crossed: result.state.audit.crossed,
              treeFingerprint: result.state.audit.treeFingerprint,
              currentFingerprint: result.state.currentFingerprint,
              staleFingerprint: result.state.staleFingerprint,
              totals: result.state.audit.totals,
            };
          } else {
            delete next[usernamePK];
          }
          this.importState = next;
          // La fila de la tabla, sustituida por la que acaba de leer el servidor.
          if (this.pointsReport && result.state.row) {
            this.pointsReport = {
              ...this.pointsReport,
              rows: this.pointsReport.rows.map((r) =>
                r.usernamePK === usernamePK ? result.state.row : r
              ),
            };
          }
          this.recordCall(
            REPORT_STEP_ID,
            false,
            `import ${action} · ${usernamePK}`,
            result
          );
        }
      );
    },

    /**
     * Los puntos de binario vivos por ETIQUETA — interna y derrame. Es la lectura
     * operativa: sobrevive a una inversión porque la etiqueta va con la persona.
     *
     * Sin lado resuelto no hay etiqueta que dar. No puede pasar (el reporte sólo
     * incluye nodos que traen `binarySide` en el árbol nuevo), pero si pasara, el
     * total entero sale bajo «interna» y nada bajo «derrame»: un número mal
     * etiquetado se discute, un número que desaparece no.
     */
    internaPoints(row: PointsReportRow): number {
      if (!row.internaSide) return row.binary.total;
      return row.binary[row.internaSide].points;
    },
    /** La pierna de DERRAME. Ver `internaPoints`. */
    derramePoints(row: PointsReportRow): number {
      if (!row.derrameSide) return 0;
      return row.binary[row.derrameSide].points;
    },

    /**
     * LO MISMO, PERO DEL ÁRBOL NUEVO: lo que la cuenta tiene escrito en
     * `binaryTree/{u}.points` de `gtr-2026`, sin transformar (`row.now`).
     *
     * Las dos de arriba (`internaPoints`/`derramePoints`) son las del árbol
     * VIEJO: cuánto traía. Éstas son las del NUEVO: cuánto tiene ya. En ceros
     * hasta que se aplica el import, y ese cero no es un fallo — el árbol coloca
     * a todo el mundo con los puntos a cero a propósito, así que marca las filas
     * que faltan por hacer.
     *
     * No hace falta cruzar nada: `row.now` ya está en el árbol nuevo, así que su
     * pierna de derrame es directamente `resolvedBinarySide`. Aplicarle el swap
     * sería cruzarlo dos veces.
     *
     * Devuelve `null` —y la tabla pinta «—»— cuando el backend no manda `now`.
     * Un cero ahí sería una afirmación («no tiene»), y lo que pasa es que no lo
     * sabemos.
     */
    internaNow(row: PointsReportRow): number | null {
      if (!row.now) return null;
      if (!row.internaSide) return row.now.left.pending + row.now.right.pending;
      return row.now[row.internaSide].pending;
    },
    /** La pierna de DERRAME de hoy. Ver `internaNow`. */
    derrameNow(row: PointsReportRow): number | null {
      if (!row.now) return null;
      if (!row.derrameSide) return 0;
      return row.now[row.derrameSide].pending;
    },
    // -----------------------------------------------------------------------
    // LAS PIERNAS DE LOS DOS ÁRBOLES, LITERALES
    // -----------------------------------------------------------------------
    // Son cuatro números y ninguno pasa por transformación alguna: la izquierda
    // y la derecha del árbol VIEJO, y la izquierda y la derecha del NUEVO, cada
    // par tal y como está guardado en su base. Es la comparación que pidió
    // Hector, y la gracia está justamente en que no haya nada en medio.
    //
    // ⚠️ NO SE USA `row.binary` PARA ESTO. `binary` son los puntos del árbol
    // viejo pero YA con las piernas intercambiadas cuando la cuenta cambió de
    // lado (es la lectura por etiqueta, la que alimenta derrame/interna). Puesto
    // aquí no sería el documento de nadie: ni el viejo (viene volteado) ni el
    // nuevo (no es lo que hay escrito). Una columna así no se puede cuadrar
    // contra ninguna base, que es exactamente para lo que existen estas dos.

    /** Izquierda del ÁRBOL VIEJO: `legacy-2026/binary-tree/{u}.points.left`. */
    viejoLeft(row: PointsReportRow): number | null {
      return row.before ? row.before.left.pending : null;
    },
    /** Derecha del ÁRBOL VIEJO. Ver `viejoLeft`. */
    viejoRight(row: PointsReportRow): number | null {
      return row.before ? row.before.right.pending : null;
    },
    /**
     * Izquierda del ÁRBOL NUEVO: `binaryTree/{u}.points.left` en `gtr-2026`.
     *
     * EN CERO HASTA QUE SE APLICA EL IMPORT, y ese cero es información, no un
     * hueco: dice qué filas quedan por hacer. Cuando se aplica aparecen las
     * cantidades, y entonces esta columna al lado de la del árbol viejo contesta
     * la única pregunta que importa — si al que cambió de pierna le cambiaron
     * también los puntos de lado.
     */
    nuevoLeft(row: PointsReportRow): number | null {
      return row.now ? row.now.left.pending : null;
    },
    /** Derecha del ÁRBOL NUEVO. Ver `nuevoLeft`. */
    nuevoRight(row: PointsReportRow): number | null {
      return row.now ? row.now.right.pending : null;
    },
    /**
     * Una cantidad que puede no saberse, ya formateada. `null` sale como «—».
     *
     * El «—» NO es un adorno: un cero ahí se leería como una afirmación («no hay
     * saldo», «no se ha restituido») y lo que pasa es que el backend no manda ese
     * campo todavía. Sirve para `now` y para `before`.
     */
    puntosOpt(value: number | null): string {
      return value === null ? "—" : this.puntos(value);
    },
    /** La hora a la que se tomó la foto, para el botón de refrescar. */
    reportTakenAt(): string {
      const at = this.pointsReport?.generatedAt;
      return at ? new Date(at).toLocaleTimeString() : "";
    },

    /**
     * Marca un paso `manual`/`pre`. `skipped: true` lo deja como N/A — el estado
     * que el 21 pide en ensayo («no hay flip de front») y el 20 permite si no
     * da tiempo. NO es lo mismo que «Hecho»: el run guarda cuál de los dos fue, y
     * eso es justo lo que se querrá saber después.
     */
    completeManual(stepId: string, skipped = false): Promise<void> {
      return this.guardedStep(
        stepId,
        this.manualAction(stepId, skipped),
        async () => {
          // Explicit null/undefined check, NOT truthiness: `0` is a legitimate
          // answer ("tardó menos de un minuto") and `0 ? …` would silently drop
          // it. The whole point of this field is that the ensayo leaves a real
          // number.
          const mins = this.minutes[stepId];
          const hasMins = mins !== undefined && mins !== null && !isNaN(mins);

          // Las claves se OMITEN, no se mandan en `undefined`: httpsCallable
          // serializa `undefined` como `null`, y la callable valida con
          // `!== undefined` ⇒ el null entra a validarse y revienta. Los dos
          // campos son opcionales, y mandarlos así los volvía obligatorios:
          // "Hecho" sin minutos ni nota daba "durationMs inválido (null)". Un
          // `pre` no tiene ninguno de los dos.
          const payload: Record<string, unknown> = {
            runId: this.runId,
            stepId,
          };
          if (hasMins) payload.durationMs = mins * 60_000;
          if (this.notes[stepId]) payload.note = this.notes[stepId];
          if (skipped) payload.skipped = true;

          await this.call("CompleteManualStep", payload);
          await this.load();
        }
      );
    },

    /**
     * `action` es la clave del SPINNER y es OPCIONAL: por defecto la del paso
     * automático. La pisa quien tiene un botón PROPIO que acaba en la misma
     * llamada —«Continuar a pesar del rojo» del gate—, porque si compartiera
     * clave con el «Ejecutar» de `StepAuto`, pulsar uno haría girar el otro:
     * los dos están en la misma tarjeta y se ven a la vez.
     */
    runAuto(payload: {
      stepId: string;
      dryRun: boolean;
      data?: unknown;
      action?: string;
    }): Promise<void> {
      return this.guardedStep(
        payload.stepId,
        payload.action ?? this.autoAction(payload.stepId, payload.dryRun),
        async () => {
          // The callable name comes from the BACKEND (`step.callable`), not from a
          // map in here. A local table is the same drift this console exists to
          // prevent: rename a callable server-side and the UI keeps calling the old
          // name — or `undefined` — and you find out on go-live day.
          const step = this.steps.find((s) => s.id === payload.stepId);
          const callable = step?.callable?.replace(/^Migration-/, "");
          if (!callable) {
            throw new Error(
              `El paso ${this.labelOf(
                payload.stepId
              )} no declara callable en el ` + `backend.`
            );
          }
          const result = await this.call<unknown>(callable, {
            runId: this.runId,
            dryRun: payload.dryRun,
            ...(payload.data as object),
          });
          if (this.isGateStep(payload.stepId)) {
            this.gateReports = {
              ...this.gateReports,
              [payload.stepId]: result as GateReport,
            };
          }
          // TODOS los pasos, no sólo el 14. El resultado se guardaba únicamente para
          // el gate, así que el dry-run de los demás no reportaba NADA: el operador
          // tenía que leerlo en el tráfico de red del navegador.
          this.recordCall(payload.stepId, payload.dryRun, callable, result);
          await this.load();
        }
      );
    },

    // -- los comandos: proyecto, trozos y portapapeles ------------------------

    /**
     * El comando TAL COMO SE VE Y SE COPIA: mismo texto para los dos, siempre.
     *
     * Sustituye SÓLO placeholders con valor conocido:
     *
     *   `<PROJECT>`   el selector.
     *   `<BUCKET>`    el bucket de backups de ESE proyecto (ver la tabla: hay
     *                 otro parecido en prod, con datos de 2022).
     *   `<FECHA-…>`   la del run, la misma que el paso 18 compara al milisegundo.
     *   `<AAAAMMDD-HHMM>` el sello del export, PEGADO por el operador — y sólo
     *                 si de verdad hay uno. Sin él el placeholder se queda a la
     *                 vista y el comando no se puede copiar: ver `needsExportStamp`.
     *
     * El resto (`<ISO8601>`, `<X>`…) se queda EN CRUDO y se lista aparte: la
     * consola no inventa valores.
     *
     * Es sólo presentación. `steps.ts` no se toca, y el dato que viaja al
     * backend tampoco: esto no se manda a ninguna callable.
     */
    withProject(raw: string): string {
      let out = raw.split(PROJECT_PLACEHOLDER).join(this.projectId);
      const date = this.migrationDateIso;
      if (date && date !== "—") out = out.split(DATE_PLACEHOLDER).join(date);
      const bucket = this.projectBucket;
      if (bucket) out = out.split(BUCKET_PLACEHOLDER).join(bucket);
      const stamp = this.exportStampValue;
      if (stamp) out = out.split(EXPORT_STAMP_PLACEHOLDER).join(stamp);
      return out;
    },
    /**
     * ¿A ESTE TEXTO LE FALTA EL SELLO DEL EXPORT?
     *
     * Lo que decide si se puede copiar. Un comando de import con el
     * `<AAAAMMDD-HHMM>` todavía puesto NO puede parecer completo: pegado tal
     * cual en una terminal no da error de sintaxis — `gcloud` lo toma como
     * nombre de carpeta y se va a buscar una que no existe, o peor, alguien
     * "arregla" el placeholder de memoria. Así que el botón se apaga y se dice
     * por qué. El texto sigue seleccionable con el ratón: esto orienta, no
     * secuestra.
     */
    needsExportStamp(text: string): boolean {
      return text.includes(EXPORT_STAMP_PLACEHOLDER);
    },
    /** El bloque entero, ya sustituido — para el botón «copiar bloque entero». */
    wholeCommand(step: MigrationStepDef): string {
      return this.withProject(step.command ?? "");
    },
    /** Los trozos copiables, o `null` si el bloque no se puede partir con seguridad. */
    commandChunks(step: MigrationStepDef): CommandChunk[] | null {
      if (!step.command) return null;
      return parseCommandBlock(this.wholeCommand(step));
    },
    /**
     * Los placeholders que SIGUEN sin resolver Y que la consola no sabe
     * rellenar. Se pintan encima del comando: descubrir que falta algo después
     * de pulsar enter cuesta un viaje al portapapeles que no hacía falta.
     *
     * El sello del export se EXCLUYE aunque siga en el texto. No porque no
     * falte, sino porque tiene su propio campo arriba y su propio aviso: meterlo
     * en esta lista, que acaba en «la consola no inventa esos valores», mandaría
     * al operador a teclearlo a mano en la terminal — justo lo que el campo
     * viene a quitar.
     */
    pendingPlaceholders(step: MigrationStepDef): string[] {
      const found = this.wholeCommand(step).match(PLACEHOLDER_RE) ?? [];
      return [...new Set(found)].filter(
        (ph) => ph !== EXPORT_STAMP_PLACEHOLDER
      );
    },
    /**
     * EL PROYECTO ESCRITO A MANO, que el selector NO cambia.
     *
     * Hay dos sitios en el runbook donde el proyecto no es un placeholder sino
     * parte del texto: `npm run build-academy` (paso 1 — el proyecto va DENTRO
     * del nombre del script) y `BUCKET=gs://gtr-academy.appspot.com` (paso 21).
     * Elegir `gtr-sandbox` arriba no los toca, y nada avisa: el build compila
     * igual y el `gsutil cp` sube igual… a producción.
     *
     * Se mira SÓLO lo ejecutable: los comentarios nombran los dos proyectos a
     * propósito (están explicando justo esta trampa) y dispararían siempre.
     */
    literalOtherProject(step: MigrationStepDef): string {
      const chunks = this.commandChunks(step);
      const code = (
        chunks
          ? chunks.map((c) => c.code)
          : this.wholeCommand(step)
              .split("\n")
              .filter((l) => !/^\s*#/.test(l))
      )
        .filter(Boolean)
        .join("\n");
      const other = MIGRATION_PROJECTS.find((p) => p.id !== this.projectId);
      if (!other) return "";
      const suffix = other.id.replace(/^gtr-/, "");
      const hits = [other.id, `build-${suffix}`].filter((needle) =>
        code.includes(needle)
      );
      if (!hits.length) return "";
      return (
        `Este paso lleva «${hits[0]}» ESCRITO A MANO: no es un placeholder y el ` +
        `selector no lo cambia. Con ${this.projectId} elegido arriba, esa línea ` +
        `sigue apuntando a ${other.id}. Cámbiala tú antes de ejecutarla.`
      );
    },
    /**
     * Copiar, con acuse de recibo visible. Si el portapapeles no está (hace
     * falta contexto seguro), se DICE — el texto sigue a la vista para copiarlo
     * a mano, pero nadie se cree que ya lo tiene.
     */
    async copyText(text: string, key: string): Promise<void> {
      if (!text) return;
      this.copyError = "";
      const ok = await writeClipboard(text);
      if (!ok) {
        this.copyError =
          "El portapapeles no está disponible (hace falta https o localhost, y " +
          "permiso del navegador). El texto sigue a la vista: selecciónalo y " +
          "cópialo a mano.";
        return;
      }
      this.copiedKey = key;
      window.setTimeout(() => {
        if (this.copiedKey === key) this.copiedKey = "";
      }, 1500);
    },
    copyMigrationDate(): Promise<void> {
      return this.copyText(this.migrationDateIso, "migration-date");
    },
    resolve(stepId: string): Promise<void> {
      return this.guardedStep(stepId, `resolve:${stepId}`, async () => {
        const name =
          stepId === "build-trees--c" ? "ResolveDraft" : "ResolveMmiDraft";
        const draft = await this.call<MigrationDraft>(name, {
          runId: this.runId,
          ...this.resolveArgs(stepId),
        });
        this.drafts = { ...this.drafts, [stepId]: draft };
        this.approved = { ...this.approved, [stepId]: false };
        // El draft se pinta en su tabla, pero el JSON crudo también se guarda: los
        // `unplaceable` y los `warnings` viven ahí y la tabla no los enseña todos.
        this.recordCall(stepId, true, `${name} (draft calculado)`, draft);
        await this.load();
      });
    },
    /**
     * The resolve inputs.
     *
     * `migrationDate` is NOT sent: the callable takes it from the RUN. It used to
     * default to `Date.now()` here while the paso-16 gate used the run's own
     * timestamp — and since INV-16 checks the very dates the draft stamps, opening
     * a run an hour before applying it would have turned the gate RED with nothing
     * actually wrong. One instant, decided once, server-side.
     */
    resolveArgs(stepId: string): Record<string, unknown> {
      return stepId === "build-trees--c"
        ? {
            anchors: this.parsedAnchors.list ?? [],
            // `aliases` NO es opcional aunque el tipo lo pinte así. Faltaba, y
            // cuando el código llevaba su propia lista `resolveDraft` lanzaba
            // SIEMPRE — el paso 15 no se podía correr desde esta consola. Esa
            // lista se retiró el 2026-07-26, así que ahora el fallo es el
            // contrario y es MUDO: mandar `{}` resuelve sin quejarse y siembra un
            // nodo para cada cuenta que debería haber sido transparente. Lo que
            // llega aquí es lo que se aplica.
            aliases: this.parsedAliases.map ?? {},
            root: "rootuser",
            copyBinaryExpiration: this.copyBinaryExpiration,
            copyInnerLegUnlocked: this.copyInnerLegUnlocked,
          }
        : { chapter: "colombia" };
    },
    dryApply(stepId: string): Promise<void> {
      return this.guardedStep(stepId, `dryApply:${stepId}`, async () => {
        const name =
          stepId === "build-trees--c" ? "ApplyDraft" : "ApplyMmiDraft";
        // SE GUARDA EL RETORNO. Antes se llamaba y se tiraba: el dry-run del apply
        // —el ensayo del paso que siembra la red— no reportaba nada, así que uno
        // correcto y uno catastrófico se veían idénticos.
        const result = await this.call(name, {
          runId: this.runId,
          dryRun: true,
        });
        this.recordCall(stepId, true, name, result);
        await this.load();
      });
    },
    apply(stepId: string): Promise<void> {
      return this.guardedStep(stepId, `apply:${stepId}`, async () => {
        const name =
          stepId === "build-trees--c" ? "ApplyDraft" : "ApplyMmiDraft";
        const result = await this.call(name, {
          runId: this.runId,
          dryRun: false,
        });
        this.recordCall(stepId, false, name, result);
        await this.load();
      });
    },

    kindLabel(kind: string): string {
      if (kind === "pre") return "prerrequisito";
      if (kind === "manual") return "manual";
      if (kind === "review") return "revisión";
      return "automático";
    },
    statusLabel(status: MigrationStepStatus): string {
      const labels: Record<MigrationStepStatus, string> = {
        pending: "pendiente",
        running: "en curso",
        done: "hecho",
        failed: "FALLÓ",
        skipped: "N/A",
      };
      return labels[status];
    },
    /**
     * EL COLOR DE CADA ESTADO.
     *
     * `running` y `skipped` NO usan `bg-warning`/`bg-info` de Bootstrap con
     * texto oscuro: sobre esos amarillos y cianes tan claros, el texto negro de
     * una insignia diminuta no se lee (Hector, 2026-07-26). Se usan tonos propios
     * lo bastante oscuros para llevar texto BLANCO — el mismo criterio que
     * `.pr-badge-ojo` en la tabla del reporte.
     */
    badgeClass(status: MigrationStepStatus): string {
      const classes: Record<MigrationStepStatus, string> = {
        pending: "bg-secondary",
        running: "pr-badge-ojo",
        done: "bg-success",
        failed: "bg-danger",
        skipped: "pr-badge-na",
      };
      return classes[status];
    },
    // El estado del BADGE, refinado para los pasos de revisión.
    //
    // «1. Calcular draft» marca el paso `running` y lo DEJA ahí hasta que se
    // aplica (el dry-run del apply, paso 2, NO lo cambia — por diseño: un dry-run
    // no completa nada). Con la etiqueta cruda «en curso» (amarillo), un operador
    // que calculó el draft y previsualizó ve el paso igual que si estuviera
    // ejecutando o colgado. Aquí, cuando ya hay draft calculado pero el paso sigue
    // `running`, se dice lo que de verdad pasa: el borrador está listo, falta
    // aplicarlo. `running` SIN draft (el resolve aún corriendo) se queda en «en
    // curso». No cambia ningún estado del backend, sólo cómo se rotula.
    isDraftReady(step: MigrationStepDef): boolean {
      return (
        step.kind === "review" &&
        this.statusOf(step.id) === "running" &&
        !!this.draftFor(step.id)
      );
    },
    stepStatusLabel(step: MigrationStepDef): string {
      if (this.isDraftReady(step)) return "borrador listo — aplícalo";
      return this.statusLabel(this.statusOf(step.id));
    },
    stepBadgeClass(step: MigrationStepDef): string {
      if (this.isDraftReady(step)) return "bg-primary";
      return this.badgeClass(this.statusOf(step.id));
    },
    /** El icono de una herramienta. Ver `TOOL_ICONS` para el porqué del mapa. */
    toolIcon(tool: MigrationCloudTool): string {
      return TOOL_ICONS[tool];
    },
    /**
     * El texto del badge de herramientas: dice QUÉ es y las NOMBRA. Los dibujos
     * se reconocen de un vistazo, pero «¿este paso pide gcloud o bq?» tiene que
     * poder contestarse sin interpretar el icono — y sin texto visible, el único
     * sitio donde cabe esa respuesta es aquí.
     */
    toolsTitle(tools: MigrationCloudTool[]): string {
      return `herramientas que hace falta tener a mano en este paso: ${tools.join(
        " · "
      )}`;
    },
    humanMs(ms?: number): string {
      if (!ms) return "";
      const min = Math.round(ms / 60_000);
      return min >= 1 ? `${min} min` : `${Math.round(ms / 1000)} s`;
    },
    pretty(value: unknown): string {
      return JSON.stringify(value, null, 2);
    },
  },
});
